import { Component } from '../../../components/Component.js';
import * as dom from '../../../core/utils/dom.js';
import { eventBus, EVENTS } from '../../../core/events/index.js';
import { storageManager } from '../../../core/storage/index.js';
import { storage } from '../../../js/utils/storage.js';
import {
  formatRelativeTime,
  getScenarioBackgroundImage,
  getLocationNameById
} from '../../../core/utils/hangoutUtils.js';
import { isPlaceholderCharacterName, pickResolvedCharacterName } from './character-name-utils.js';
import { TemplateLoader } from './templates/templateLoader.js';
import { getCurrentUserId } from '../../../js/utils/auth_utils.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import { getLastRoundSpriteTuple, reconstructFinalStateFromConversation, parseConversationData, normalizeScenarioId, resolveConversationScenarioName, resolveActionFromConversationMetadata } from './state-utils.js';
import {
  buildCarouselCharacters,
  buildConversationCarouselCharacters,
  MAX_PARTY_SIZE,
  normalizePartyIds,
  fillEmptyPartySlotsFromScenario
} from './carousel-utils.js';
import { normalizeGender } from '../../../js/utils/genderUtils.js';
import {
  hasCharacterPlaceholder,
  resolveTextPlaceholders
} from '../../../js/utils/placeholder-resolver.js';
import {
  buildScenarioButton,
  buildPrivateScenarioButton,
  buildConversationButton,
  buildCreateScenarioCard,
  buildSpotsHeader,
  buildViewTabs,
  buildSpotFiltersPanel,
  buildCharCountFilter,
  syncCharCountFilter,
  buildSpotRow,
  getSpotsViewDom,
  spotMatchesFilters,
  suggestCharCountFromParty,
  getScenarioCharCount
} from './spots-builder.js';
import { getCurrentLanguage, interpolate } from '../../../js/translations.js';
import { tagMappingService } from '../../../services/TagMappingService.js';
import { renderScenarioTagPills } from '../../../js/create/tags/scenarioTagPills.js';
import { buildResumeConfig, buildNewConversationConfig } from './hangout-config-builder.js';
import {
  filterOutfitsByMembership,
  classifyScenarioOutfits,
  appendClassifiedOutfitElements,
  setActiveOutfitOnList
} from './inventory-utils.js';
import { perkTierForUser, tierHasGroupConversations } from '../../../js/utils/membershipUsage.js';
import {
  sortScenariosByCharacterRelevance,
  mapScenariosToSpots,
  mapScenarioRecord,
  shuffleArray,
  resolveConversationBackdrop,
  resolveScenarioRecordDisplay,
  resolveScenarioDisplayText,
  hydrateScenarioRecord,
  overlaySelectedCharacterSlots
} from '../services/scenarioMappers.js';
import {
  filterScenariosForSfw,
  filterScenarioTagOptionsForSfw,
  getBlockedNsfwScenarioMessage,
  scenarioIsNsfw
} from '../../../js/utils/nsfwTags.js';
import { getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { showToast } from '../../../js/utils/toast.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';
import {
  appendModerationKebab,
  closeContentModerationMenu
} from '../../../js/moderation/contentModerationMenu.js';
import {
  filterBlockedScenarios,
  isCharacterBlocked,
  isScenarioBlocked,
  subscribeBlockedContent
} from '../../../js/moderation/blockedContentStore.js';
import {
  DEFAULT_CHARACTER_PLACEHOLDER,
  whenImageReady,
  loadFallbackImage,
  fetchStandingSpriteFallback,
  fetchModalCharacterSpriteUrl
} from './modal-image-utils.js';

const HANGOUT_URL = './hangout.html';

const toggleHidden = (el, hidden) => {
  if (!el) return;
  el.classList.toggle('is-hidden', !!hidden);
};

const isConversationLastMessageTooltip = (spotButton, title) =>
  spotButton?.getAttribute('data-spot-type') === 'premium' || title === 'Last Message';

const appendDescriptionTooltipTags = (tooltip, labels) => {
  if (!tooltip || !labels?.length) return;
  const lang = getCurrentLanguage();
  const t = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
  const tagsRow = document.createElement('div');
  tagsRow.className = 'description-tooltip-tags';
  tagsRow.setAttribute('aria-label', t['rankings.scenarioTags'] || 'Scenario tags');
  tagsRow.setAttribute('data-translate-attr', 'aria-label:rankings.scenarioTags');
  labels.forEach((label) => {
    const pill = document.createElement('span');
    pill.className = 'scenario-tag-pill is-selected';
    pill.textContent = label;
    tagsRow.appendChild(pill);
  });
  tooltip.appendChild(tagsRow);
};

const listedOutfitIdsForScenarioChar = (char) => {
  const ids = [];
  const push = (value) => {
    const n = parseInt(String(value), 10);
    if (!Number.isFinite(n) || n === 0) return;
    if (!ids.includes(n)) ids.push(n);
  };
  const listed = char?.family ?? char?.outfits ?? char?.outfit_ids;
  if (Array.isArray(listed)) listed.forEach(push);
  push(char?.outfit ?? char?.outfit_id);
  return ids;
};

const pickScenarioCharJsonName = (char) => {
  if (!char || typeof char !== 'object') return '';
  return String(char.name || char.character_name || char.char_name || '').trim();
};

/**
 * @param {'outfit' | 'multiCharacter'} context
 * @param {Record<string, string>} t
 */
const resolveMembershipLockHint = (context, t) => {
  switch (context) {
    case 'outfit':
      return t['rankings.lockedHintUnlockOutfits'] || 'Upgrade to unlock all outfits';
    case 'multiCharacter':
      return t['rankings.lockedHintUnlockMultiCharacter'] || 'Upgrade to unlock multi-character scenarios';
    default: {
      const _exhaustive = /** @type {never} */ (context);
      return _exhaustive;
    }
  }
};

const INFO_SKIP_KEYS = new Set(['race', 'lora_activation']);

const formatInfoLabel = (key) =>
  String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const valueToDisplayString = (val) => {
  if (Array.isArray(val)) {
    return val
      .map((item) => (typeof item === 'string' ? item : item?.name ?? item))
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v).trim())
      .join(', ');
  }
  return String(val);
};

const POV_TAG_LABELS = {
  any: ['AnyPOV', 'Any POV'],
  female: ['Female POV'],
  male: ['Male POV'],
};

const toTagIdSet = (ids) => new Set(
  (Array.isArray(ids) ? ids : []).map((id) => Number(id)).filter((id) => Number.isFinite(id))
);

const spotHasTagId = (spot, idSet) => (
  (Array.isArray(spot?.tags) ? spot.tags : []).some((tag) => idSet.has(Number(tag)))
);

const resolvePovTagIds = () => ({
  any: toTagIdSet(tagMappingService.resolveScenarioTagIds(POV_TAG_LABELS.any)),
  female: toTagIdSet(tagMappingService.resolveScenarioTagIds(POV_TAG_LABELS.female)),
  male: toTagIdSet(tagMappingService.resolveScenarioTagIds(POV_TAG_LABELS.male)),
});

const scenarioMatchesViewerGender = (spot, charGender, povIds) => {
  const hasAny = spotHasTagId(spot, povIds.any);
  const hasFemale = spotHasTagId(spot, povIds.female);
  const hasMale = spotHasTagId(spot, povIds.male);
  if (hasAny || (hasFemale && hasMale)) return true;
  if (hasFemale || hasMale) {
    const gender = normalizeGender(charGender);
    if (hasFemale && gender === 'female') return true;
    if (hasMale && gender === 'male') return true;
    return false;
  }
  return spot.first_char_gender === 'all'
    || normalizeGender(spot.first_char_gender) === normalizeGender(charGender);
};

const collectInfoEntries = (info) => {
  if (!info || typeof info !== 'object' || Array.isArray(info)) return [];
  return Object.entries(info).flatMap(([key, val]) => {
    if (INFO_SKIP_KEYS.has(key)) return [];
    if (val == null || val === '') return [];
    if (typeof val === 'object' && !Array.isArray(val)) return [];
    const text = valueToDisplayString(val).trim();
    if (!text) return [];
    return [{ key, label: formatInfoLabel(key), text }];
  });
};

export class CharacterModalComponent extends Component {
  constructor(element, options = {}) {
    super(element, options);

    this.rankingsState = options.state;
    this.service = options.service;
    this.onPickAdditionalModeChange = options.onPickAdditionalModeChange || null;

    this.state = {
      selectedCharacterId: null,
      selectedOutfitId: null,
      spotsView: 'scenarios'
    };

    this.character = null;
    this.characterId = null;
    this.isPrivateCharacter = false;
    this.selectedScenarioId = null;
    this.carouselCharacters = [];
    this.isConversationMode = false;
    this.isLoadingConversationSprite = false;

    /** @type {Array<string|number>} */
    this.partyCharacterIds = [];
    this.pickAdditionalMode = false;
    this.pendingPartySlotIndex = null;

    /** @type {number[]} */
    this.selectedFilterTagIds = [];
    /** @type {'1'|'2'|'3'|'any'} */
    this.charCountFilter = 'any';
    this.spotFiltersPanelOpen = false;
    /** @type {Array<{ id: number, label: string, tooltip?: string }>|null} */
    this._scenarioFilterTagOptions = null;

    this.scenarioDetailsCache = new Map();
    this.characterOutfitsCache = new Map();

    this.lastLanguageChange = 0;
    this.lastSpotClick = 0;
    this.debounceDelay = 100;

    this.spotsRenderGeneration = 0;
    this.unsubscribeBlockedContent = subscribeBlockedContent((detail) => {
      this.handleBlockedContentChanged(detail);
    });

    this.bindMethods();
    this.init().catch(console.error);
  }

  bindMethods() {
    const methods = [
      'handleConfirmStart', 'handleSpotClick',
      'handleLanguageChange', 'handleMappingsUpdated',
      'handleOutsideClick', 'handleRetry', 'handleEscapeKey',
      'handleCharacterContentScroll', 'handleScrollTopClick'
    ];
    methods.forEach(method => this[method] = this[method].bind(this));
    this.handleCharacterSelectedBound = this.handleCharacterSelected.bind(this);
  }

  getActiveSpotsView() {
    return this.state.spotsView || 'scenarios';
  }

  setSpotsView(view) {
    this.state.spotsView = (view === 'conversations' || view === 'private') ? view : 'scenarios';
  }

  async init() {
    await this.render();
    this.addEventListeners();
    eventBus.on(EVENTS.RANKINGS.CHARACTER_SELECTED, this.handleCharacterSelectedBound);

    document.removeEventListener('languageChanged', this.handleLanguageChange);
    document.addEventListener('languageChanged', this.handleLanguageChange);

    document.removeEventListener('mappingsUpdated', this.handleMappingsUpdated);
    document.addEventListener('mappingsUpdated', this.handleMappingsUpdated);

    if (!TemplateLoader.preloadingPromise) {
      TemplateLoader.preloadTemplates().catch(console.warn);
    }
  }

  async handleLanguageChange(e) {
    const now = Date.now();
    if (now - this.lastLanguageChange < this.debounceDelay || !e.detail?.language) return;

    this.lastLanguageChange = now;

    if (!(/** @type {any} */ (window)).applyTranslations) return;

    if (this.selectedScenarioId) {
      await this.updateTraitsForLanguageChange();
    }
  }

  async handleMappingsUpdated(e) {
    const { language } = e.detail || {};
    if (!language) return;

    this._scenarioFilterTagOptions = null;

    if (this.spotFiltersPanelOpen) {
      const panel = this.characterContent?.querySelector('.spot-filters-panel');
      if (panel) {
        await this._ensureScenarioFilterTagOptions();
        this._refreshSpotFiltersPanel(panel);
      }
    }

    if (this.selectedScenarioId) {
      await this.updateTraitsForLanguageChange();
    }
  }

  async updateTraitsForLanguageChange() {
    if (!this.selectedScenarioId) return;
    try {
      await this.renderTraits();
    } catch (error) {
      console.error('updateTraitsForLanguageChange:', error);
    }
  }

  async render() {
    const template = await TemplateLoader.loadTemplate('character-modal-base');
    dom.html(this.element, template);
    this.cacheModalElements();
  }

  cacheModalElements() {
    /** @type {HTMLElement} */
    const element = /** @type {HTMLElement} */ (this.element);
    this.modalContent = element.querySelector('.modal-content');
    this.characterLoading = element.querySelector('.character-loading');
    this.characterError = element.querySelector('.character-error');
    this.characterContent = element.querySelector('.character-content');
    this.retryButton = element.querySelector('.retry-btn');
    this.ensureModalChrome();
  }

  addEventListeners() {
    if (this.element) {
      this.element.addEventListener('click', this.handleOutsideClick);
    }

    if (this.retryButton) {
      this.retryButton.addEventListener('click', this.handleRetry);
    }

    this.characterContent?.addEventListener('scroll', this.handleCharacterContentScroll, { passive: true });
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  handleEscapeKey(e) {
    if (e.key !== 'Escape') return;
    if (this.isVisible()) {
      this.hide({ clearParty: true });
      return;
    }
    if (this.pickAdditionalMode) {
      this.clearPickAdditionalMode();
    }
  }

  handleOutsideClick(e) {
    if (e.target === this.element) {
      this.hide({ clearParty: true });
    }
  }

  handleRetry() {
    if (this.characterId) {
      this.loadCharacterDetails(this.characterId, { isPrivate: this.isPrivateCharacter });
    }
  }

  /**
   * @param {number|Object|null} characterInput
   * @param {{ skipAutoOpenFirstSpot?: boolean, isPrivate?: boolean, forceRefresh?: boolean, preserveParty?: boolean }} options
   */
  async show(characterInput = null, options = {}) {
    const { characterId, character } = this.parseCharacterInput(characterInput);
    const shouldSkipAutoOpenFirstSpot = !!options.skipAutoOpenFirstSpot;
    const isPrivate = !!options.isPrivate;
    const preserveParty = !!options.preserveParty;
    if (!characterId) return;

    this.isPrivateCharacter = isPrivate;

    if (isPrivate) {
      this.setSpotsView('private');
    } else if (!preserveParty) {
      this.setSpotsView('scenarios');
    }
    this.character = character;

    if (!preserveParty) {
      this.partyCharacterIds = [characterId];
      this.clearPickAdditionalMode();
    } else if (!this.partyCharacterIds.length) {
      this.partyCharacterIds = [characterId];
    } else if (!this.partyCharacterIds.some((id) => String(id) === String(characterId))) {
      this.partyCharacterIds[0] = characterId;
    }

    if (this.characterId !== characterId || !this.character || options.forceRefresh) {
      this.characterId = characterId;
      await this.loadCharacterDetails(characterId, options);
    } else {
      this.characterId = characterId;
    }

    this.fetchCharacterOutfitsAsync(characterId);
    this.displayModal();
    await this.renderCharacterDetails();

    if (this.selectedScenarioId && (preserveParty || !shouldSkipAutoOpenFirstSpot)) {
      const scenarioDetails = await this.getScenarioDetailsCached(this.selectedScenarioId);
      if (this.partyCharacterIds.length > 1 || scenarioDetails?.chars?.length > 1) {
        await this.syncPartyDisplay();
      }
    }

    if (!shouldSkipAutoOpenFirstSpot) {
      this.autoOpenFirstSpotForActiveView();
    }
  }

  parseCharacterInput(characterInput) {
    if (typeof characterInput === 'object' && characterInput?.id) {
      return { characterId: characterInput.id, character: characterInput };
    }
    return { characterId: characterInput || this.state.selectedCharacterId, character: null };
  }

  async fetchCharacterOutfitsAsync(characterId) {
    const outfits = await this.service.getCharacterOutfits(characterId);
    if (outfits) {
      this.characterOutfitsCache.set(characterId, outfits);
      if (this.characterId === characterId && this.isVisible()) {
        await this.renderTraits();
      }
    }
    return outfits;
  }

  autoOpenFirstSpotForActiveView() {
    setTimeout(() => {
      const { firstSpotSelector } = getSpotsViewDom(this.getActiveSpotsView());
      const firstButton = /** @type {HTMLElement | null} */ (
        this.characterContent?.querySelector(`${firstSpotSelector}:not(.is-membership-locked)`)
      );
      if (!firstButton) return;
      const firstSpotId = firstButton.getAttribute('data-spot-id');
      if (
        firstButton.classList.contains('selected')
        && this.selectedScenarioId != null
        && String(this.selectedScenarioId) === String(firstSpotId)
      ) {
        return;
      }
      firstButton.dispatchEvent(new Event('click', { bubbles: true }));
    }, 100);
  }

  displayModal() {
    this.element.classList.add('is-open');
    document.body.classList.add('modal-open');

    const modalContent = this.element.querySelector('.character-modal-content');
    modalContent?.querySelectorAll('.modal-close-button').forEach(btn => btn.remove());
    if (this.characterContent) this.characterContent.scrollTop = 0;
    this.ensureModalChrome();
    this.updateScrollTopVisibility();
  }

  hide({ clearParty = false } = {}) {
    this.element.classList.remove('is-open');
    document.body.classList.remove('modal-open');
    if (clearParty) {
      this.partyCharacterIds = [];
      this.clearPickAdditionalMode();
    }
  }

  isVisible() {
    return this.element.classList.contains('is-open');
  }

  async loadCharacterDetails(characterId, options = {}) {
    this.showLoadingState();

    try {
      const nameHint = pickResolvedCharacterName(this.character, characterId) || undefined;
      this.character = await this.service.getCharacterDetails(
        options.isPrivate || nameHint
          ? {
              id: characterId,
              ...(nameHint ? { name: nameHint } : {}),
              ...(options.isPrivate ? { private: true, rank: 'Private' } : {})
            }
          : characterId,
        { forceRefresh: !!options.forceRefresh }
      );
      const scenariosResponse = await this.service.getScenarios();
      const allScenarios = filterScenariosForSfw(scenariosResponse?.data || []);

      if (!allScenarios.length) {
        throw new Error('NO_SCENARIOS');
      }

      const sortedScenarios = sortScenariosByCharacterRelevance(allScenarios, characterId);
      const scenarioIds = sortedScenarios.map(s => String(s.id));
      const imageUrls = await this.service.getBatchImageUrls(scenarioIds);
      this.character.publicSpots = filterBlockedScenarios(mapScenariosToSpots(
        sortedScenarios,
        imageUrls,
        (loc, time) => this.service.getBackgroundUrl(loc, time),
        [this.rankingsState?.characters, this.character],
        this.getSelectedPartyDisplayChars()
      ));

      await this.setInitialModalImage();
      this.showContentState();
      await this.renderCharacterDetails();
    } catch (error) {
      console.error('Failed to load character details:', error);
      this.showErrorState();
    }
  }

  lookupSelectedCharacterName(characterId) {
    const sameId = (character) => character && String(character.id ?? character.character_id) === String(characterId);
    const carouselName = pickResolvedCharacterName(
      (this.carouselCharacters || []).find(sameId),
      characterId
    );
    if (carouselName) return carouselName;
    if (sameId(this.character)) {
      const openName = pickResolvedCharacterName(this.character, characterId);
      if (openName) return openName;
    }
    return pickResolvedCharacterName(
      (this.rankingsState?.characters || []).find(sameId),
      characterId
    );
  }

  getSelectedPartyDisplayChars() {
    const ids = (this.partyCharacterIds?.length
      ? this.partyCharacterIds
      : (this.characterId != null ? [this.characterId] : [])
    ).slice(0, MAX_PARTY_SIZE);
    return ids.map((id) => ({
      id,
      name: this.lookupSelectedCharacterName(id)
    }));
  }

  resolveSpotDisplayFields(spot, selectedChars = this.getSelectedPartyDisplayChars()) {
    const sourceName = typeof spot?.tokenName === 'string' ? spot.tokenName : (spot?.name || '');
    const sourceDescription = typeof spot?.tokenDescription === 'string'
      ? spot.tokenDescription
      : (spot?.description || '');
    const hydrated = hydrateScenarioRecord(
      { chars: Array.isArray(spot?.chars) ? spot.chars : [] },
      [this.rankingsState?.characters, this.character]
    );
    const displayChars = overlaySelectedCharacterSlots(hydrated?.chars, selectedChars);
    return {
      name: resolveScenarioDisplayText(sourceName, displayChars),
      description: resolveScenarioDisplayText(sourceDescription, displayChars)
    };
  }

  getSpotById(spotId) {
    if (!spotId || !this.character) return null;
    const spotIdString = String(spotId);
    const publicSpots = Array.isArray(this.character.publicSpots) ? this.character.publicSpots : [];
    return publicSpots.find((spot) => String(spot?.id) === spotIdString) || null;
  }

  _findScenarioRecordOnPage(scenarioId) {
    if (scenarioId == null || scenarioId === '') return null;
    const spotIdString = String(scenarioId);
    const publicSpot = this.getSpotById(spotIdString);
    if (publicSpot) return publicSpot;
    const scenarios = this.service?.rankingsState?.scenarios || [];
    return scenarios.find((scenario) => String(scenario?.id) === spotIdString) || null;
  }

  applyScenarioNavigationFilters(scenarioId) {
    const record = this._findScenarioRecordOnPage(scenarioId);
    if (!record) return false;

    this.charCountFilter = /** @type {'1'|'2'|'3'|'any'} */ (suggestCharCountFromParty(getScenarioCharCount(record)));
    this.selectedFilterTagIds = tagMappingService.resolveScenarioTagIds(record.tags || []);

    const panel = this.characterContent?.querySelector('.spot-filters-panel');
    this._refreshSpotFiltersPanel(panel);
    this.applyActiveScenarioFilters();
    return true;
  }

  async getScenarioDetailsCached(spotId) {
    if (!spotId) return null;
    const cacheKey = String(spotId);
    if (this.scenarioDetailsCache.has(cacheKey)) return this.scenarioDetailsCache.get(cacheKey);

    const requestPromise = this.service.getScenarioDetails(spotId)
      .catch((error) => {
        this.scenarioDetailsCache.delete(cacheKey);
        throw error;
      });

    this.scenarioDetailsCache.set(cacheKey, requestPromise);
    return requestPromise;
  }

  async setInitialModalImage() {
    if (this.character.publicSpots?.length > 0) {
      const initialSpotId = this.character.publicSpots[0].id;
      if (!this.selectedScenarioId) {
        this.selectedScenarioId = initialSpotId;
      }
    } else {
      this.character.modalImage = this.character.image || this.character.front_card_preview_url || DEFAULT_CHARACTER_PLACEHOLDER;
    }
  }

  getBridgeImageUrl() {
    if (this.character?.front_card_preview_url) {
      return this.character.front_card_preview_url;
    }

    const image = this.character?.image;
    if (image && image !== DEFAULT_CHARACTER_PLACEHOLDER) {
      return image;
    }

    return '';
  }

  showLoadingState() {
    toggleHidden(this.characterLoading, false);
    toggleHidden(this.characterContent, true);
    toggleHidden(this.characterError, true);
  }

  showContentState() {
    toggleHidden(this.characterContent, false);
    /** @type {HTMLElement} */ (this.characterContent).offsetHeight;
    toggleHidden(this.characterLoading, true);
  }

  showErrorState() {
    toggleHidden(this.characterLoading, true);
    toggleHidden(this.characterError, false);
  }

  async renderCharacterDetails() {
    if (!this.character) {
      throw new Error('NO_CHARACTER');
    }

    const characterData = this.getCharacterDisplayData();
    this.updateCharacterHeader(characterData);

    try {
      const bridgeImage = this.getBridgeImageUrl();
      const template = await TemplateLoader.getPopulatedTemplate('character-details', {
        characterHeaderName: characterData.name,
        characterName: characterData.name,
        characterImage: bridgeImage
      });

      dom.html(this.characterContent, template);

      const characterImg = this.characterContent.querySelector('.character-primary-column .character-image')
        || this.characterContent.querySelector('.character-image');
      const primaryImageWrap = this.characterContent.querySelector('.character-primary-column .character-modal-image')
        || this.characterContent.querySelector('.character-modal-image');
      if (primaryImageWrap) {
        this.appendCharacterInfoExpandIcon(
          primaryImageWrap,
          this.characterId,
          characterData.name
        );
      }
      if (characterImg && characterImg instanceof HTMLImageElement) {
        if (!bridgeImage) {
          characterImg.removeAttribute('src');
          characterImg.style.opacity = '0';
        } else {
          characterImg.addEventListener('error', () => {
            const fallbackSrc = this.character.image !== characterImg.src
              ? this.character.image
              : (this.character.front_card_preview_url || DEFAULT_CHARACTER_PLACEHOLDER);
            loadFallbackImage(characterImg, fallbackSrc);
          }, { once: true });
        }
      }

      // Keep creator node for header-height parity across party columns; hide visually.
      this.characterContent.querySelectorAll('.character-creator').forEach((el) => {
        el.classList.add('is-visually-hidden');
        el.setAttribute('aria-hidden', 'true');
        if (!el.textContent?.trim()) el.textContent = '\u00a0';
      });

      if (!this.characterContent.querySelector('.character-modal-right')) {
        throw new Error('MODAL_RIGHT_MISSING');
      }

      this.renderTraits();
      await this.renderPublicSpots();
      this.updateCharacterImage();
      this.renderGoButton();
      await this.renderPartySlots();
      this.restoreSelectedSpotHighlight();
    } catch (error) {
      console.error('Error rendering character details:', error);
      await this.renderErrorTemplate();
    }
  }

  restoreSelectedSpotHighlight() {
    if (!this.selectedScenarioId || !this.characterContent) return;
    const selectedId = String(this.selectedScenarioId);
    this.characterContent.querySelectorAll('.spot-button').forEach((btn) => {
      const spotId = btn.getAttribute('data-spot-id');
      btn.classList.toggle('selected', String(spotId) === selectedId);
    });
  }

  getCharacterDisplayData() {
    const resolvedName = pickResolvedCharacterName(this.character, this.character?.id)
      || this.character?.name
      || (this.character?.id != null ? `Character ${this.character.id}` : 'Character');
    return {
      name: resolvedName,
      creator: this.character.creator || 'Kissandra.ai',
      image: this.character.modalImage || this.character.image || this.character.front_card_preview_url || DEFAULT_CHARACTER_PLACEHOLDER
    };
  }

  updateCharacterHeader(characterData) {
    /** @type {HTMLElement} */
    const header = /** @type {HTMLElement} */ (document.querySelector('.character-modal-left .character-header h2'));
    if (header) {
      header.innerText = characterData.name;
    }
  }

  async renderErrorTemplate() {
    const errorElement = await TemplateLoader.cloneFragment('error-states', 'error');
    this.characterContent.replaceChildren(errorElement);

    const retryBtn = dom.$('.retry-btn', /** @type {any} */(this.characterContent));
    retryBtn?.addEventListener('click', () => this.loadCharacterDetails(this.characterId, { isPrivate: this.isPrivateCharacter }));
  }

  isUserLoggedIn() {
    try {
      const userData = storage.get('userData') || {};
      return userData.isLoggedIn === true && !!(window.tokenManager?.getAccessToken?.() ?? null);
    } catch {
      return false;
    }
  }

  canAddPartyCharacters() {
    if (!this.isUserLoggedIn()) return false;
    const tier = perkTierForUser(storage.get('userData') || {});
    return tierHasGroupConversations(tier);
  }


  _normalizePartyIds() {
    this.partyCharacterIds = normalizePartyIds(this.partyCharacterIds, MAX_PARTY_SIZE, {
      sortById: !this.isConversationMode
    });
    if (this.partyCharacterIds.length > 0) {
      this.characterId = this.partyCharacterIds[0];
      this.state.selectedCharacterId = this.partyCharacterIds[0];
    }
  }

  _fillEmptyPartySlotsFromScenario(scenarioDetails) {
    const { partyIds, changed } = fillEmptyPartySlotsFromScenario(
      this.partyCharacterIds,
      scenarioDetails,
      MAX_PARTY_SIZE
    );
    this.partyCharacterIds = partyIds;
    if (this.partyCharacterIds.length > 0) {
      this.characterId = this.partyCharacterIds[0];
      this.state.selectedCharacterId = this.partyCharacterIds[0];
    }
    return changed;
  }

  _syncCharCountFromParty() {
    this.charCountFilter = /** @type {'1'|'2'|'3'|'any'} */ (suggestCharCountFromParty(this.partyCharacterIds?.length || 0));
  }

  async renderPartySlots() {
    const slotsContainer = this.characterContent?.querySelector('.character-party-slots');
    const partyColumns = this.characterContent?.querySelector('.character-party-columns');
    const primaryColumn = this.characterContent?.querySelector('.character-primary-column');
    if (!slotsContainer || !partyColumns || !primaryColumn) return;

    this._normalizePartyIds();
    if (!this.partyCharacterIds.length && this.characterId) {
      this.partyCharacterIds = [this.characterId];
    }

    primaryColumn.querySelectorAll('.character-party-slot-close').forEach(el => el.remove());
    if (this.partyCharacterIds.length > 0) {
      const primaryClose = document.createElement('button');
      primaryClose.type = 'button';
      primaryClose.className = 'character-party-slot-close';
      primaryClose.setAttribute('aria-label', 'Remove character');
      primaryClose.innerHTML = '&times;';
      primaryClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleRemovePartyCharacterAt(0);
      });
      primaryColumn.appendChild(primaryClose);
    }

    // Remove previous filled columns (keep primary)
    partyColumns.querySelectorAll('.character-party-column.filled-party-slot').forEach(el => el.remove());
    slotsContainer.innerHTML = '';

    const canAdd = this.canAddPartyCharacters();
    const emptySlotCount = Math.max(0, MAX_PARTY_SIZE - this.partyCharacterIds.length);

    for (let i = 1; i < this.partyCharacterIds.length; i++) {
      const charId = this.partyCharacterIds[i];
      const slot = await this.createFilledPartySlot(charId, i);
      partyColumns.appendChild(slot);
    }

    for (let i = 0; i < emptySlotCount; i++) {
      const slotIndex = this.partyCharacterIds.length + i;
      slotsContainer.appendChild(this.createEmptyPartySlot(slotIndex, canAdd));
    }
  }

  async createFilledPartySlot(characterId, slotIndex) {
    const slot = document.createElement('div');
    slot.className = 'character-party-column filled-party-slot';
    slot.setAttribute('data-party-slot', String(slotIndex));
    slot.setAttribute('data-character-id', String(characterId));

    let name = `Character ${characterId}`;
    let imageUrl = DEFAULT_CHARACTER_PLACEHOLDER;
    let outfitId = this.state.selectedOutfitId || '0';

    const partyMatch = this.carouselCharacters?.find(c => String(c.id) === String(characterId));
    if (partyMatch) {
      name = partyMatch.name || name;
      imageUrl = partyMatch.modalImage || imageUrl;
      outfitId = partyMatch.outfitId != null ? String(partyMatch.outfitId) : outfitId;
    } else {
      try {
        const details = await this.service.getCharacterDetails(characterId);
        name = details?.name || name;
        imageUrl = details?.front_card_preview_url || details?.image || imageUrl;
        if (this.selectedScenarioId) {
          imageUrl = await this.service.getScenarioSprite(
            characterId,
            this.selectedScenarioId,
            imageUrl,
            outfitId
          );
        }
      } catch (error) {
        console.warn('Failed to load party slot character', characterId, error);
      }
    }

    const header = document.createElement('div');
    header.className = 'character-header';
    const title = document.createElement('h2');
    title.className = 'character-name-header';
    title.textContent = name;
    const creator = document.createElement('p');
    creator.className = 'character-creator';
    creator.style.visibility = 'hidden';
    creator.setAttribute('aria-hidden', 'true');
    creator.textContent = '\u00a0';
    header.appendChild(title);
    header.appendChild(creator);

    const imageWrap = document.createElement('div');
    imageWrap.className = 'character-modal-image';

    const charImg = document.createElement('img');
    charImg.className = 'character-image';
    charImg.src = imageUrl;
    charImg.alt = name;
    charImg.style.transition = 'opacity 0.3s ease';
    charImg.style.position = 'relative';
    charImg.style.zIndex = '1';
    charImg.onerror = () => { charImg.src = DEFAULT_CHARACTER_PLACEHOLDER; };
    imageWrap.appendChild(charImg);
    this.appendCharacterInfoExpandIcon(imageWrap, characterId, name);

    const footer = document.createElement('div');
    footer.className = 'character-footer-section';
    const traitsContainer = document.createElement('div');
    traitsContainer.className = 'character-traits';
    footer.appendChild(traitsContainer);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'character-party-slot-close';
    closeBtn.setAttribute('aria-label', `Remove ${name}`);
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleRemovePartyCharacterAt(slotIndex);
    });

    slot.appendChild(header);
    slot.appendChild(imageWrap);
    slot.appendChild(footer);
    slot.appendChild(closeBtn);

    await this.renderPartySlotTraits(traitsContainer, characterId, outfitId, partyMatch);
    return slot;
  }

  getSharedBackgroundElement() {
    return /** @type {HTMLImageElement | null} */ (
      this.characterContent?.querySelector('.character-party-shared-bg')
      || this.characterContent?.querySelector('.character-party-stage > .character-background')
    );
  }

  async renderPartySlotTraits(traitsContainer, characterId, selectedOutfitId, partyMatch = null) {
    if (!traitsContainer) return;

    if (this.getActiveSpotsView() === 'conversations') {
      traitsContainer.classList.add('is-hidden');
      return;
    }
    traitsContainer.classList.remove('is-hidden');

    const traitsList = document.createElement('div');
    traitsList.className = 'traits-list';
    traitsList.dataset.partyCharacterId = String(characterId);
    traitsContainer.replaceChildren(traitsList);

    const outfitData = await this.getPartySlotOutfitData(characterId, partyMatch);
    const isConversationMode = this.isConversationMode;
    const allRendered = await appendClassifiedOutfitElements(traitsList, outfitData, {
      maxOutfits: 12,
      createElement: (outfitId, isOwned, lockReason) =>
        this.createOutfitElement(outfitId, isConversationMode, isOwned, lockReason, characterId)
    });
    setActiveOutfitOnList(traitsList, allRendered, selectedOutfitId);
  }

  async getPartySlotOutfitData(characterId, partyMatch = null) {
    const primaryOutfits = await this.getScenarioOutfitsWithInventory();
    const hasPrimaryOutfits = (
      primaryOutfits.owned.length
      + primaryOutfits.unownedByMembership.length
      + primaryOutfits.unownedByCharacter.length
    ) > 0;

    const partyAvailable = Array.isArray(partyMatch?.availableOutfits)
      ? partyMatch.availableOutfits.map(id => parseInt(String(id), 10)).filter(Number.isFinite)
      : [];

    if (!partyAvailable.length) {
      // Scenario has no per-character outfit data — copy primary traits.
      return hasPrimaryOutfits
        ? primaryOutfits
        : { owned: [0], unownedByMembership: [], unownedByCharacter: [] };
    }

    try {
      let characterOutfits = this.characterOutfitsCache.get(characterId);
      if (!characterOutfits) {
        characterOutfits = await this.service.getCharacterOutfits(characterId);
        if (characterOutfits) this.characterOutfitsCache.set(characterId, characterOutfits);
      }
      return classifyScenarioOutfits(partyAvailable, characterOutfits);
    } catch {
      return hasPrimaryOutfits
        ? primaryOutfits
        : { owned: partyAvailable, unownedByMembership: [], unownedByCharacter: [] };
    }
  }

  createEmptyPartySlot(slotIndex, canAdd) {
    const slot = document.createElement('div');
    slot.className = 'character-party-slot add-character-slot';
    slot.setAttribute('role', 'button');
    slot.setAttribute('tabindex', '0');
    slot.setAttribute('data-party-slot', String(slotIndex));
    slot.setAttribute('aria-label', 'Add character');
    if (!canAdd) {
      slot.setAttribute('aria-disabled', 'true');
    }

    slot.innerHTML = `
      <div class="card-inner">
        <div class="card-front create-character-front">
          <div class="create-character-plus" aria-hidden="true">+</div>
          <div class="create-character-title">Add character</div>
        </div>
      </div>
    `;

    const handleActivate = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleAddPartySlotClick(slotIndex);
    };

    slot.addEventListener('click', handleActivate);
    slot.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      handleActivate(e);
    });

    return slot;
  }

  async handleAddPartySlotClick(slotIndex) {
    if (!this.canAddPartyCharacters()) {
      await this.showAddCharacterGatedCta();
      return;
    }
    this.enterPickAdditionalMode(slotIndex);
  }

  async showAddCharacterGatedCta() {
    try {
      const { showGroupConversationRequiredModal } = await import('../../../js/utils/quotaErrorHandler.js');
      await showGroupConversationRequiredModal({ context: 'rankings-add-character' });
    } catch (error) {
      console.warn('Could not show paid-required modal:', error);
    }
  }

  enterPickAdditionalMode(slotIndex) {
    this.pickAdditionalMode = true;
    this.pendingPartySlotIndex = slotIndex;
    this.hide({ clearParty: false });
    if (typeof this.onPickAdditionalModeChange === 'function') {
      this.onPickAdditionalModeChange({
        active: true,
        partyCharacterIds: [...this.partyCharacterIds]
      });
    }
  }

  clearPickAdditionalMode() {
    const wasActive = this.pickAdditionalMode;
    this.pickAdditionalMode = false;
    this.pendingPartySlotIndex = null;
    if (wasActive && typeof this.onPickAdditionalModeChange === 'function') {
      this.onPickAdditionalModeChange({ active: false, partyCharacterIds: [] });
    }
  }

  isPickAdditionalMode() {
    return !!this.pickAdditionalMode;
  }

  getPartyCharacterIds() {
    return [...(this.partyCharacterIds || [])];
  }

  async addPartyCharacter(characterId) {
    if (characterId == null || characterId === '') return false;

    this._normalizePartyIds();
    if (this.partyCharacterIds.some(id => String(id) === String(characterId))) {
      this.clearPickAdditionalMode();
      return false;
    }
    if (this.partyCharacterIds.length >= MAX_PARTY_SIZE) {
      this.clearPickAdditionalMode();
      return false;
    }

    this.partyCharacterIds.push(characterId);
    this._normalizePartyIds();
    if (this.spotFiltersPanelOpen) {
      this._syncCharCountFromParty();
      const panel = this.characterContent?.querySelector('.spot-filters-panel');
      this._refreshSpotFiltersPanel(panel);
      this.applyActiveScenarioFilters();
    }
    this.clearPickAdditionalMode();

    const primaryId = this.partyCharacterIds[0];
    await this.show(primaryId, {
      skipAutoOpenFirstSpot: true,
      preserveParty: true,
      isPrivate: this.isPrivateCharacter
    });
    return true;
  }

  async handleRemovePartyCharacterAt(slotIndex) {
    this._normalizePartyIds();
    if (slotIndex < 0 || slotIndex >= this.partyCharacterIds.length) return;

    // Capture before splice/normalize — normalizePartyIds overwrites this.characterId.
    const previousPrimaryId = this.characterId;
    const removedPrimary = slotIndex === 0;

    this.partyCharacterIds.splice(slotIndex, 1);
    this._normalizePartyIds();

    if (this.spotFiltersPanelOpen) {
      this._syncCharCountFromParty();
      const panel = this.characterContent?.querySelector('.spot-filters-panel');
      this._refreshSpotFiltersPanel(panel);
      this.applyActiveScenarioFilters();
    }

    if (!this.partyCharacterIds.length) {
      this.hide({ clearParty: true });
      this.character = null;
      this.characterId = null;
      this.carouselCharacters = [];
      return;
    }

    const newPrimaryId = this.partyCharacterIds[0];
    const primaryChanged = removedPrimary
      || String(newPrimaryId) !== String(previousPrimaryId);

    if (primaryChanged) {
      // Do not forceRefresh: that skips rankings-state names and can fall back to
      // `Character ${id}`. show() still reloads because character input is an id only
      // (this.character is cleared) while characterId already matches the new primary.
      await this.show(newPrimaryId, {
        skipAutoOpenFirstSpot: true,
        preserveParty: true,
        isPrivate: this.isPrivateCharacter
      });
      return;
    }

    this.carouselCharacters = (this.carouselCharacters || []).filter(
      c => this.partyCharacterIds.some(id => String(id) === String(c.id))
    );

    if (this.selectedScenarioId && this.partyCharacterIds.length > 1) {
      await this.syncPartyDisplay();
    } else {
      await this.renderPartySlots();
      this.updateCharacterImage();
    }
  }

  _rankingsCopy() {
    const lang = getCurrentLanguage();
    return window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
  }

  ensureModalChrome() {
    const modalContent = this.element?.querySelector('.character-modal-content');
    if (!modalContent) return;

    if (!this.scrollTopButton?.isConnected) {
      this.scrollTopButton?.remove();
      const scrollTopButton = document.createElement('button');
      scrollTopButton.type = 'button';
      scrollTopButton.className = 'character-modal-scroll-top';
      scrollTopButton.setAttribute('aria-label', this._rankingsCopy()['rankings.modal.backToTop'] || 'Back to top');
      scrollTopButton.setAttribute('data-translate-attr', 'aria-label:rankings.modal.backToTop');
      scrollTopButton.addEventListener('click', this.handleScrollTopClick);
      modalContent.appendChild(scrollTopButton);
      this.scrollTopButton = scrollTopButton;
    }

    this.renderGoButton();
  }

  handleScrollTopClick() {
    if (this.characterContent) this.characterContent.scrollTop = 0;
    this.updateScrollTopVisibility();
  }

  handleCharacterContentScroll() {
    this.updateScrollTopVisibility();
  }

  updateScrollTopVisibility() {
    const scrolled = (this.characterContent?.scrollTop || 0) >= window.innerHeight;
    this.scrollTopButton?.classList.toggle('is-visible', scrolled);
  }

  renderGoButton() {
    const modalContent = this.element?.querySelector('.character-modal-content');
    if (!modalContent) return;

    let goButton = modalContent.querySelector('.confirm-go');
    if (!goButton) {
      goButton = document.createElement('button');
      goButton.className = 'confirmation-button confirm-go';
      goButton.textContent = this._rankingsCopy()['rankings.modal.startConversation'] || 'Start Conversation';
      goButton.setAttribute('data-translate', 'rankings.modal.startConversation');
      goButton.addEventListener('click', this.handleConfirmStart);
      modalContent.appendChild(goButton);
    }

    const hasSelection = this.selectedScenarioId != null && this.selectedScenarioId !== '';
    goButton.classList.toggle('is-hidden', !hasSelection);
  }

  clearScenarioSelection() {
    this.selectedScenarioId = null;
    this.selectedConversationData = null;
    this.isConversationMode = false;
    this.characterContent?.querySelectorAll('.spot-button').forEach((btn) => {
      btn.classList.remove('selected');
    });
    this.renderGoButton();
  }

  async renderTraits() {
    if (!this.character) return;

    /** @type {HTMLElement} */
    const traitsContainer = /** @type {HTMLElement} */ (
      this.characterContent.querySelector('.character-primary-column .character-traits')
      || this.characterContent.querySelector('.character-traits')
    );
    if (!traitsContainer) return;

    if (this.getActiveSpotsView() === 'conversations') {
      traitsContainer.classList.add('is-hidden');
      this.characterContent.querySelectorAll('.filled-party-slot .character-traits').forEach((el) => {
        el.classList.add('is-hidden');
      });
      return;
    }

    traitsContainer.classList.remove('is-hidden');
    this.characterContent.querySelectorAll('.filled-party-slot .character-traits').forEach((el) => {
      el.classList.remove('is-hidden');
    });

    let traitsList = traitsContainer.querySelector('.traits-list');
    if (!traitsList) {
      traitsList = document.createElement('div');
      traitsList.className = 'traits-list';
      traitsList.id = 'outfit-list';
      traitsContainer.appendChild(traitsList);
    } else {
      traitsList.innerHTML = '';
    }

    const isConversationMode = this.isConversationMode;

    let outfitData;
    if (isConversationMode) {
      const membershipData = this.getConversationOutfitsWithInventory();
      outfitData = { owned: membershipData.owned, unownedByMembership: membershipData.unowned, unownedByCharacter: [] };
    } else {
      outfitData = await this.getScenarioOutfitsWithInventory();
    }

    const allRenderedOutfits = await appendClassifiedOutfitElements(traitsList, outfitData, {
      maxOutfits: 5,
      createElement: (outfitId, isOwned, lockReason) =>
        this.createOutfitElement(outfitId, isConversationMode, isOwned, lockReason)
    });
    this.state.selectedOutfitId = setActiveOutfitOnList(traitsList, allRenderedOutfits, this.state.selectedOutfitId);
  }

  getConversationOutfitsWithInventory() {
    const characterOutfits = this.characterOutfitsCache.get(this.characterId);
    const selectedId = parseInt(this.state.selectedOutfitId) || 0;
    const outfitList = characterOutfits
      ? Array.from(characterOutfits)
      : [selectedId];
    if (!outfitList.includes(selectedId)) outfitList.unshift(selectedId);
    return filterOutfitsByMembership(outfitList);
  }

  async getScenarioOutfitsWithInventory() {
    if (!this.selectedScenarioId) return { owned: [], unownedByMembership: [], unownedByCharacter: [] };

    const scenarioDetails = await this.getScenarioDetailsCached(this.selectedScenarioId);
    const rawOutfits = scenarioDetails?.outfits || [];
    const outfitList = rawOutfits.map(id => parseInt(String(id), 10)).filter(id => !isNaN(id));
    if (!outfitList.includes(0)) outfitList.unshift(0);

    const characterOutfits = this.characterOutfitsCache.get(this.characterId);
    return classifyScenarioOutfits(outfitList, characterOutfits);
  }

  async showCharacterMissingOutfitMessage(outfitName) {
    const { showLocked } = await import('../../../js/utils/UnifiedNotificationSystem.js');

    const characterName = this.character?.name || 'Character';
    const characterId = this.character?.id;
    const outfitId = this.state.selectedOutfitId;

    const characterImageContainer = this.characterContent.querySelector('.character-modal-left') ||
      this.characterContent.querySelector('.character-image');

    const createUrl = `/create-video.html?mode=sprite-text&char=${encodeURIComponent(characterId)}&outfit=${encodeURIComponent(outfitId)}`;

    showLocked(`${characterName} does not yet have ${outfitName}`, {
      hint: 'Click here to create it',
      relativeTo: characterImageContainer,
      closable: false,
      duration: 5000,
      onClick: () => window.router?.navigate(createUrl) || (window.location.href = createUrl)
    });
  }

  /**
   * @param {string} itemName
   * @param {{ context?: 'outfit' | 'multiCharacter' }} [options]
   */
  async showLockedOutfitMessage(itemName, { context = 'outfit' } = {}) {
    /** @type {any} */
    const { showLocked } = await import('../../../js/utils/UnifiedNotificationSystem.js');

    const characterImageContainer = this.characterContent.querySelector('.character-modal-left') ||
      this.characterContent.querySelector('.character-image');

    const lang = getCurrentLanguage();
    const t = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
    const titleTemplate = t['rankings.lockedRequiresPaidMembership']
      || '{name} requires a paid membership';

    showLocked(interpolate(titleTemplate, { name: itemName }), {
      hint: resolveMembershipLockHint(context, t),
      relativeTo: characterImageContainer,
      closable: false,
      duration: 4000,
      onClick: () => window.router?.navigate('/pricing') || (window.location.href = '/pricing.html')
    });

    if (!this.isUserLoggedIn()) return;

    try {
      if (context === 'multiCharacter') {
        const { showGroupConversationRequiredModal } = await import('../../../js/utils/quotaErrorHandler.js');
        await showGroupConversationRequiredModal({ context: 'rankings-multi-character' });
        return;
      }
      const { showQuotaExhaustedModal } = await import('../../../js/utils/quotaErrorHandler.js');
      await showQuotaExhaustedModal();
    } catch (modalError) {
      console.warn('Could not show upgrade modal:', modalError);
    }
  }

  async createOutfitElement(outfitId, isConversationMode, isOwned = true, lockReason = null, partyCharacterId = null) {
    const traitElement = document.createElement('span');
    traitElement.className = `trait-tag ${!isOwned ? 'locked-outfit' : ''}`;
    traitElement.dataset.outfitId = outfitId;
    if (partyCharacterId != null) {
      traitElement.dataset.partyCharacterId = String(partyCharacterId);
    }

    const outfitName = await this.service.getOutfitName(outfitId);

    if (!isOwned) {
      traitElement.dataset.lockReason = lockReason || 'membership';

      const tooltip = lockReason === 'character'
        ? `${outfitName} - Character does not yet have this outfit`
        : `${outfitName} - Not available in inventory (preview only)`;

      traitElement.title = tooltip;

      const lockIcon = document.createElement('i');
      lockIcon.className = 'fas fa-lock';

      traitElement.appendChild(lockIcon);
      traitElement.appendChild(document.createTextNode(outfitName));
    } else {
      traitElement.textContent = outfitName;
    }

    traitElement.addEventListener('click', () => {
      if (partyCharacterId != null && String(partyCharacterId) !== String(this.characterId)) {
        this.handlePartyOutfitSelection(partyCharacterId, outfitId, isConversationMode);
        return;
      }
      this.handleOutfitSelection(outfitId, isConversationMode);
    });

    return traitElement;
  }

  async handleOutfitSelection(outfitId, isConversationMode) {
    const traitsList = this.characterContent.querySelector('.character-primary-column #outfit-list')
      || this.characterContent.querySelector('#outfit-list');

    if (traitsList) {
      setActiveOutfitOnList(traitsList, [outfitId], outfitId);
    }

    this.state.selectedOutfitId = String(outfitId);

    const primaryParty = this.carouselCharacters?.find(c => String(c.id) === String(this.characterId));
    if (primaryParty) primaryParty.outfitId = String(outfitId);

    if (isConversationMode) {
      await this.loadConversationSprite(this.selectedConversationData, outfitId);
    } else {
      await this.updateSpriteForOutfit(outfitId);
    }

    if (primaryParty) {
      primaryParty.modalImage = this.character.modalImage;
    }
  }

  async handlePartyOutfitSelection(characterId, outfitId, isConversationMode) {
    const traitsList = this.characterContent.querySelector(
      `.filled-party-slot[data-character-id="${characterId}"] .traits-list`
    );
    if (traitsList) {
      setActiveOutfitOnList(traitsList, [outfitId], outfitId);
    }

    const partyChar = this.carouselCharacters?.find(c => String(c.id) === String(characterId));
    if (!partyChar) return;

    partyChar.outfitId = String(outfitId);

    try {
      let spriteUrl;
      if (isConversationMode) {
        const lastAction = partyChar.lastAction || partyChar.lastSpriteState || null;
        spriteUrl = await fetchModalCharacterSpriteUrl(partyChar.id, lastAction, outfitId);
      } else if (this.selectedScenarioId) {
        spriteUrl = await this.service.getScenarioSprite(
          partyChar.id,
          this.selectedScenarioId,
          DEFAULT_CHARACTER_PLACEHOLDER,
          String(outfitId)
        );
      } else {
        return;
      }

      partyChar.modalImage = spriteUrl;
      const img = /** @type {HTMLImageElement | null} */ (
        this.characterContent.querySelector(
          `.filled-party-slot[data-character-id="${characterId}"] .character-image`
        )
      );
      if (img) img.src = partyChar.modalImage;
    } catch (error) {
      console.error('handlePartyOutfitSelection:', error);
    }
  }

  async updateSpriteForOutfit(outfitId) {
    if (this.isLoadingConversationSprite || !this.character?.id || !this.selectedScenarioId) {
      return;
    }

    try {
      const spriteUrl = await this.service.getScenarioSprite(
        this.character.id,
        this.selectedScenarioId,
        DEFAULT_CHARACTER_PLACEHOLDER,
        String(outfitId)
      );
      this.character.modalImage = spriteUrl;
    } catch (error) {
      console.error('Failed to update sprite:', error);
      this.character.modalImage = this.character.front_card_preview_url || DEFAULT_CHARACTER_PLACEHOLDER;
    }

    this.updateCharacterImage();
  }

  async loadConversationSprite(conversationData, outfitId = '0', { withBackdrop = false } = {}) {
    if (!conversationData) {
      console.warn('loadConversationSprite: no conversation data');
      return;
    }

    try {
      if (withBackdrop) this.isLoadingConversationSprite = true;

      const conversation = parseConversationData(conversationData);
      if (!conversation?.conversationId) {
        console.warn('loadConversationSprite: no valid conversation data');
        if (withBackdrop) this.isLoadingConversationSprite = false;
        return;
      }

      if (withBackdrop && conversation.isMultiCharacter && conversation.allCharacters?.length > 1) {
        await this.initializeConversationCarousel(conversation);
        this.isLoadingConversationSprite = false;
        return;
      }

      const tuple = getLastRoundSpriteTuple(conversation, conversation.characterId);
      let lastOutfit = withBackdrop ? (tuple.outfitId || outfitId) : outfitId;
      let lastAction = tuple.actionId;

      if (lastAction === null) {
        try {
          const metadata = storage.getConversationMetadata(conversation.conversationId);
          const resolved = resolveActionFromConversationMetadata(metadata);
          lastAction = resolved.actionId;
          if (withBackdrop && resolved.outfitId) lastOutfit = resolved.outfitId;
        } catch (error) {
          console.warn('loadConversationSprite: metadata', error);
        }
      }

      const spriteUrl = await fetchModalCharacterSpriteUrl(
        conversation.characterId,
        lastAction,
        lastOutfit
      );

      this.character.modalImage = spriteUrl;
      this.updateCharacterImage();

      if (withBackdrop) {
        const finalState = reconstructFinalStateFromConversation(conversation);
        let scenarioDetails = null;
        try {
          if (conversation.scenarioId && conversation.scenarioId !== 'unknown') {
            scenarioDetails = await this.getScenarioDetailsCached(conversation.scenarioId);
          }
        } catch {
          scenarioDetails = null;
        }
        const { locId, timeSlot } = resolveConversationBackdrop(scenarioDetails, finalState);
        this.updateCharacterBackground(locId, timeSlot);
        this.state.selectedOutfitId = lastOutfit;
        this.isLoadingConversationSprite = false;
      }
    } catch (error) {
      console.error('loadConversationSprite:', error);
      if (withBackdrop) this.isLoadingConversationSprite = false;

      try {
        const fallbackSprite = await fetchStandingSpriteFallback(this.character, outfitId);
        this.character.modalImage = fallbackSprite;
        this.updateCharacterImage();
      } catch (fallbackError) {
        console.error('loadConversationSprite fallback:', fallbackError);
        if (withBackdrop) {
          this.character.modalImage = DEFAULT_CHARACTER_PLACEHOLDER;
          this.updateCharacterImage();
        }
      }
    }
  }

  async renderPublicSpots() {
    if (!this.character) return;
    const activeView = this.getActiveSpotsView();
    if (activeView === 'scenarios' && (!this.character.publicSpots || !this.character.publicSpots.length)) {
      return;
    }

    try {
      let spotsWrapper = this.characterContent.querySelector('.spots-container-wrapper');
      if (!spotsWrapper) {
        spotsWrapper = document.createElement('div');
        spotsWrapper.className = 'spots-container-wrapper';
        const modalRight = this.characterContent.querySelector('.character-modal-right');
        if (!modalRight) {
          console.error('renderPublicSpots: modal right missing');
          return;
        }
        modalRight.appendChild(spotsWrapper);
      }

      let mainSpotsContainer = spotsWrapper.querySelector('.main-spots-container');
      if (!mainSpotsContainer) {
        mainSpotsContainer = document.createElement('div');
        mainSpotsContainer.className = 'main-spots-container';
        spotsWrapper.appendChild(mainSpotsContainer);
      }

      const headers = mainSpotsContainer.querySelectorAll('.spots-header-container');
      if (headers.length > 1) {
        headers.forEach((el, idx) => { if (idx > 0) el.remove(); });
      }
      const tabRows = mainSpotsContainer.querySelectorAll('.spots-view-tabs');
      if (tabRows.length > 1) {
        tabRows.forEach((el, idx) => { if (idx > 0) el.remove(); });
      }
      const filterPanels = mainSpotsContainer.querySelectorAll('.spot-filters-panel');
      if (filterPanels.length > 1) {
        filterPanels.forEach((el, idx) => { if (idx > 0) el.remove(); });
      }
      mainSpotsContainer.querySelectorAll(':scope > .spot-search-bar').forEach((el) => el.remove());
      const contentContainers = mainSpotsContainer.querySelectorAll('.spots-content-container');
      if (contentContainers.length > 1) {
        contentContainers.forEach((el, idx) => { if (idx > 0) el.remove(); });
      }

      const headerContainer = buildSpotsHeader({
        onSearchClick: () => this.toggleSearchBar(),
        onShuffleClick: () => this.handleShuffleScenarios()
      });

      const existingHeader = mainSpotsContainer.querySelector('.spots-header-container');
      if (existingHeader) {
        existingHeader.replaceWith(headerContainer);
      } else {
        mainSpotsContainer.appendChild(headerContainer);
      }

      const viewTabs = buildViewTabs(activeView, (view) => {
        if (this.getActiveSpotsView() === view) return;
        this.setSpotsView(view);
        this.renderPublicSpots();
      });

      const existingTabs = mainSpotsContainer.querySelector('.spots-view-tabs');
      if (existingTabs) {
        existingTabs.replaceWith(viewTabs);
      } else {
        headerContainer.insertAdjacentElement('afterend', viewTabs);
      }

      const { searchId, panelId } = getSpotsViewDom(this.getActiveSpotsView());

      let filtersPanel = mainSpotsContainer.querySelector('.spot-filters-panel');
      if (!filtersPanel) {
        filtersPanel = await this._buildSpotFiltersPanel(panelId, searchId);
        mainSpotsContainer.appendChild(filtersPanel);
      } else {
        filtersPanel.id = panelId;
        const searchBar = filtersPanel.querySelector('.spot-search-bar');
        if (searchBar) {
          searchBar.id = searchId;
          searchBar.setAttribute('name', searchId);
        }
        this._refreshSpotFiltersPanel(filtersPanel);
      }
      filtersPanel.classList.toggle('active', this.spotFiltersPanelOpen);

      let contentContainer = mainSpotsContainer.querySelector('.spots-content-container');
      if (!contentContainer) {
        contentContainer = document.createElement('div');
        contentContainer.className = 'spots-content-container';
        mainSpotsContainer.appendChild(contentContainer);
      } else {
        contentContainer.innerHTML = '';
      }
      contentContainer.id = 'spots-content-container';

      const currentGen = ++this.spotsRenderGeneration;

      if (this.getActiveSpotsView() === 'conversations') {
        await this.renderConversationsView(contentContainer, currentGen);
      } else if (this.getActiveSpotsView() === 'private') {
        await this.renderPrivateScenariosView(contentContainer, currentGen);
      } else {
        await this.renderScenariosView(contentContainer, currentGen);
      }

      if (/** @type {any} */ (window).applyTranslations) {
        const currentLang = /** @type {any} */ (window).getCurrentLanguage();
        /** @type {any} */ (window).applyTranslations('rankings', currentLang);
        const heading = mainSpotsContainer.querySelector('.spots-title');
        if (heading) this._formatSpotsTitleForMobile(heading);
      }

      const searchInput = /** @type {HTMLInputElement} */ (filtersPanel.querySelector('.spot-search-bar'));
      if (searchInput) {
        searchInput.oninput = null;
        searchInput.oninput = () => {
          this.applyActiveScenarioFilters();
        };
      }
    } catch (error) {
      console.error('renderPublicSpots:', error);
    }
  }

  async _ensureScenarioFilterTagOptions() {
    if (this._scenarioFilterTagOptions?.length) return this._scenarioFilterTagOptions;
    try {
      this._scenarioFilterTagOptions = filterScenarioTagOptionsForSfw(
        await tagMappingService.getAllScenarioTagOptions()
      );
    } catch (error) {
      console.warn('Failed to load scenario tag options', error);
      this._scenarioFilterTagOptions = [];
    }
    return this._scenarioFilterTagOptions;
  }

  async _buildSpotFiltersPanel(panelId, searchId) {
    const tagsPicker = document.createElement('div');
    tagsPicker.className = 'scenario-tags-picker spot-tags-picker';
    tagsPicker.setAttribute('aria-label', 'Filter by scenario tags');
    tagsPicker.setAttribute('data-translate-attr', 'aria-label:rankings.filterByScenarioTags');

    const charCountFilter = buildCharCountFilter({
      selected: this.charCountFilter,
      onChange: (value) => {
        this.charCountFilter = value;
        syncCharCountFilter(charCountFilter, value);
        this.applyActiveScenarioFilters();
      }
    });

    const panel = buildSpotFiltersPanel({
      panelId,
      searchId,
      tagsPicker,
      charCountFilter
    });

    const options = await this._ensureScenarioFilterTagOptions();
    const handleTagChange = (nextIds) => {
      this.selectedFilterTagIds = nextIds;
      renderScenarioTagPills(tagsPicker, options, {
        selectedIds: this.selectedFilterTagIds,
        maxTags: Infinity,
        onChange: handleTagChange
      });
      this.applyActiveScenarioFilters();
    };
    renderScenarioTagPills(tagsPicker, options, {
      selectedIds: this.selectedFilterTagIds,
      maxTags: Infinity,
      onChange: handleTagChange
    });

    return panel;
  }

  _refreshSpotFiltersPanel(filtersPanel) {
    if (!filtersPanel) return;
    const charRow = filtersPanel.querySelector('.spot-char-count-filter');
    syncCharCountFilter(charRow, this.charCountFilter);
    const tagsPicker = filtersPanel.querySelector('.spot-tags-picker');
    if (tagsPicker && this._scenarioFilterTagOptions) {
      renderScenarioTagPills(tagsPicker, this._scenarioFilterTagOptions, {
        selectedIds: this.selectedFilterTagIds,
        maxTags: Infinity,
        onChange: (nextIds) => {
          this.selectedFilterTagIds = nextIds;
          this._refreshSpotFiltersPanel(filtersPanel);
          this.applyActiveScenarioFilters();
        }
      });
    }
  }

  applyActiveScenarioFilters() {
    if (this.getActiveSpotsView() === 'conversations') {
      this.filterConversations();
      return;
    }
    if (this.getActiveSpotsView() === 'private') {
      this.filterPrivateScenarios();
      return;
    }
    this.filterSpots();
  }

  _formatSpotsTitleForMobile(headingEl) {
    if (!headingEl || window.innerWidth > 768) return;
    if (headingEl.querySelector('.title-word')) return;
    const text = headingEl.textContent || '';
    const words = text.trim().split(/\s+/);
    if (words.length <= 1) return;
    headingEl.innerHTML = words.map(w => `<span class="title-word">${w}</span>`).join('');
  }

  async renderScenariosView(container, gen) {
    const spotRow = buildSpotRow('public-spot-row');

    if (gen && gen !== this.spotsRenderGeneration) return;
    container.replaceChildren(spotRow);

    let sortedSpots = filterBlockedScenarios(this.character.publicSpots);

    if (this.character?.gender != null) {
      try {
        await tagMappingService.loadStaticMapping('scenarioTags');
      } catch (error) {
        console.warn('scenario POV tags unavailable', error);
      }
      const povIds = resolvePovTagIds();
      sortedSpots = sortedSpots.filter((spot) => scenarioMatchesViewerGender(spot, this.character.gender, povIds));
    }

    if (gen && gen !== this.spotsRenderGeneration) return;

    const createCard = buildCreateScenarioCard();
    spotRow.appendChild(createCard);

    const lockMultiCharacter = !this.canAddPartyCharacters();
    const selectedChars = this.getSelectedPartyDisplayChars();
    const buttons = await Promise.all(sortedSpots.map((spot) => {
      const display = this.resolveSpotDisplayFields(spot, selectedChars);
      return buildScenarioButton({ ...spot, ...display }, {
        onSpotClick: this.handleSpotClick,
        onDescriptionClick: (name, desc, el) => this.showDescriptionModal(name, desc, el),
        membershipLocked: lockMultiCharacter && getScenarioCharCount(spot) > 1
      });
    }));

    if (gen && gen !== this.spotsRenderGeneration) return;
    buttons.forEach((spotButton) => {
      if (spotButton) spotRow.insertBefore(spotButton, createCard);
    });
    this.filterSpots();
  }

  cachePrivateScenarios(privateScenarios) {
    const rankingsState = this.service?.rankingsState;
    if (!rankingsState?.setScenarios) return;
    const publicScenarios = (rankingsState.scenarios || []).filter(s => !s.is_private);
    rankingsState.setScenarios([
      ...publicScenarios,
      ...filterScenariosForSfw(privateScenarios).map((scenario) => hydrateScenarioRecord(
        mapScenarioRecord(scenario, { isPrivate: true }),
        [this.rankingsState?.characters, this.character]
      ))
    ]);
  }

  async renderConversationsView(container, gen) {
    const spotRow = buildSpotRow('premium-spot-row');
    if (gen && gen !== this.spotsRenderGeneration) return;
    container.replaceChildren(spotRow);

    try {
      spotRow.replaceChildren(await TemplateLoader.cloneFragment('error-states', 'conversations-loading'));
    } catch (templateError) {
      console.error('Failed to load conversations loading template', templateError);
    }

    try {
      const { HangoutState } = await import('../../hangout/state/HangoutState.js');
      const hangoutState = HangoutState.getInstance();
      this._normalizePartyIds();
      const filterCharacterIds = (this.partyCharacterIds?.length
        ? this.partyCharacterIds
        : [this.characterId]
      ).filter(id => id != null);
      const { conversations: recentConversations = [] } = await hangoutState.getRecentConversations({
        characterIds: filterCharacterIds
      });

      spotRow.innerHTML = '';
      if (gen && gen !== this.spotsRenderGeneration) return;
      await this.renderConversationButtons(recentConversations, spotRow);
    } catch (err) {
      console.error('Failed to load recent conversations', err);

      try {
        const errorElement = await TemplateLoader.cloneFragment('error-states', 'conversations-error');
        spotRow.replaceChildren(errorElement);
      } catch (templateError) {
        console.error('Failed to load conversations error template', templateError);
      }
    }
  }

  async renderPrivateScenariosView(container, gen) {
    const spotRow = buildSpotRow('private-scenarios-row');
    if (gen && gen !== this.spotsRenderGeneration) return;
    container.replaceChildren(spotRow);

    try {
      spotRow.replaceChildren(await TemplateLoader.cloneFragment('error-states', 'private-scenarios-loading'));

      const userId = getCurrentUserId();
      if (!userId) {
        spotRow.replaceChildren(await TemplateLoader.cloneFragment('error-states', 'no-private-scenarios-login'));
        return;
      }

      const privateScenarios = await this.service.getUserPrivateScenarios(userId);

      if (privateScenarios && privateScenarios.length > 0) {
        this.cachePrivateScenarios(privateScenarios);
      }

      spotRow.innerHTML = '';
      if (gen && gen !== this.spotsRenderGeneration) return;

      if (!privateScenarios || privateScenarios.length === 0) {
        if (gen && gen !== this.spotsRenderGeneration) return;
        spotRow.replaceChildren(await TemplateLoader.cloneFragment('error-states', 'no-private-scenarios'));
        return;
      }

      let imageUrls = {};
      try {
        const scenarioIds = privateScenarios.map(s => String(s.id ?? s.scenario_id ?? '').trim()).filter(Boolean);
        imageUrls = await this.service.getBatchImageUrls(scenarioIds);
      } catch (e) {
        console.warn('Failed to fetch private scenario image URLs', e);
        imageUrls = {};
      }

      if (gen && gen !== this.spotsRenderGeneration) return;

      const lockMultiCharacter = !this.canAddPartyCharacters();
      const selectedChars = this.getSelectedPartyDisplayChars();
      const nameSources = [this.rankingsState?.characters, this.character];
      const buttons = await Promise.all(filterBlockedScenarios(privateScenarios).map((scenario) => {
        const id = String(scenario?.id ?? scenario?.scenario_id ?? '').trim();
        const resolved = resolveScenarioRecordDisplay(
          mapScenarioRecord(scenario, { isPrivate: true }),
          nameSources,
          selectedChars
        );
        resolved.imageUrl = (id && imageUrls && imageUrls[id]) ? imageUrls[id] : '';
        return buildPrivateScenarioButton(resolved, {
          onSpotClick: this.handleSpotClick,
          onDescriptionClick: (name, desc, el) => this.showDescriptionModal(name, desc, el),
          membershipLocked: lockMultiCharacter && getScenarioCharCount(scenario) > 1
        });
      }));

      if (gen && gen !== this.spotsRenderGeneration) return;
      buttons.forEach((spotButton) => {
        if (spotButton) spotRow.appendChild(spotButton);
      });
      this.filterPrivateScenarios();

    } catch (err) {
      console.error('Failed to load private scenarios', err);
      try {
        spotRow.replaceChildren(await TemplateLoader.cloneFragment('error-states', 'private-scenarios-error'));
      } catch (templateError) {
        console.error('Failed to load private scenarios error template', templateError);
      }
    }
  }

  async renderConversationButtons(recentConversations, premiumSpotRow) {
    if (recentConversations && recentConversations.length > 0) {
      let allScenarios = Array.isArray(this.service?.rankingsState?.scenarios)
        ? this.service.rankingsState.scenarios
        : [];
      if (!allScenarios.length) {
        try {
          const scenariosResponse = await this.service.getScenarios();
          allScenarios = Array.isArray(scenariosResponse?.data) ? scenariosResponse.data : [];
        } catch (error) {
          console.warn('Failed to preload scenarios for conversation name mapping', error);
          allScenarios = [];
        }
      }

      const scenarioNameById = new Map(
        allScenarios
          .filter((scenario) => scenario && scenario.id != null && typeof scenario.name === 'string' && scenario.name.trim())
          .map((scenario) => [String(scenario.id), scenario.name.trim()])
      );

      for (const conversation of recentConversations) {
        if (isCharacterBlocked(conversation.characterId) || isScenarioBlocked(conversation.scenarioId)) continue;
        let displayName = conversation.characterName || 'Unknown Character';
        if (this.rankingsState?.characters && conversation.characterId) {
          const stateChar = this.rankingsState.characters.find(c => String(c.id) === String(conversation.characterId));
          if (stateChar?.name) displayName = stateChar.name;
        }

        let backgroundImage;
        let scenarioDetails = null;
        try {
          const finalState = reconstructFinalStateFromConversation(conversation);
          if (conversation.scenarioId && conversation.scenarioId !== 'unknown') {
            scenarioDetails = await this.getScenarioDetailsCached(conversation.scenarioId);
          }
          const { locId, timeOfDay } = resolveConversationBackdrop(scenarioDetails, finalState);
          backgroundImage = await getScenarioBackgroundImage(null, locId, timeOfDay);
        } catch {
          backgroundImage = await getScenarioBackgroundImage(null, 0, 'night');
        }

        const conversationChars = Array.isArray(conversation.allCharacters) ? conversation.allCharacters : [];
        const conversationScenarioChars = Array.isArray(scenarioDetails?.chars) ? scenarioDetails.chars : [];
        const conversationDisplayChars = overlaySelectedCharacterSlots(conversationScenarioChars, conversationChars);
        const mappedScenarioName = resolveScenarioDisplayText(
          scenarioNameById.get(normalizeScenarioId(conversation.scenarioId)) || '',
          conversationDisplayChars
        );
        const resolvedScenarioName = resolveConversationScenarioName({
          mappedName: mappedScenarioName,
          detailName: resolveScenarioDisplayText(scenarioDetails?.name, conversationDisplayChars),
          conversationName: conversation.scenarioName
        });

        const conversationButton = buildConversationButton({ ...conversation, displayName, scenarioName: resolvedScenarioName }, {
          backgroundImage,
          onSpotClick: this.handleSpotClick,
          onDescriptionClick: (name, desc, el) => this.showDescriptionModal(name, desc, el),
          formatRelativeTime
        });
        premiumSpotRow.appendChild(conversationButton);
      }
    } else {
      const placeholderElement = await TemplateLoader.cloneFragment('error-states', 'no-conversations', {
        characterName: this.character?.name || 'this character'
      });
      placeholderElement.setAttribute('data-translate', 'rankings.noConversationsWithCharacter');
      premiumSpotRow.appendChild(placeholderElement);
    }
  }

  filterSpots() {
    /** @type {HTMLInputElement} */
    const searchBar = /** @type {HTMLInputElement} */ (this.characterContent.querySelector('#public-spot-search'));
    const searchTerm = (searchBar?.value || '').toLowerCase().trim();
    const spots = this.character.publicSpots;
    const spotRow = this.characterContent.querySelector('#public-spot-row');
    const fallbackCharacterName = this.character?.name || 'Unknown';
    const hasActiveFilters = searchTerm !== ''
      || this.selectedFilterTagIds.length > 0
      || this.charCountFilter !== 'any';

    if (!spots || !spotRow) return;

    /** @type {NodeListOf<HTMLElement>} */
    const spotButtons = /** @type {NodeListOf<HTMLElement>} */ (spotRow.querySelectorAll('.spot-button'));
    spotButtons.forEach(async button => {
      const spotId = button.getAttribute('data-spot-id');
      const spotType = button.getAttribute('data-spot-type');

      if (spotType === 'create') {
        toggleHidden(button, hasActiveFilters);
        return;
      }
      const normalizedSpotId = String(spotId ?? '');
      const spot = spots.find(s => String(s.id) === normalizedSpotId);
      if (!spot) {
        toggleHidden(button, true);
        return;
      }

      let descriptionText = spot.tokenDescription || spot.description || '';
      let scenarioChars = Array.isArray(spot.chars) ? spot.chars : [];
      const needsDescription = !descriptionText
        || descriptionText.trim().length < 10
        || (hasCharacterPlaceholder(descriptionText) && scenarioChars.length === 0);
      const needsTags = !Array.isArray(spot.tags) || spot.tags.length === 0;
      const needsChars = scenarioChars.length === 0;

      if (needsDescription || needsTags || needsChars) {
        try {
          const scenarioDetails = await this.getScenarioDetailsCached(spotId);
          if (scenarioDetails?.description) {
            descriptionText = scenarioDetails.description;
          }
          if (Array.isArray(scenarioDetails?.chars) && scenarioDetails.chars.length > 0) {
            scenarioChars = scenarioDetails.chars;
            spot.chars = scenarioChars;
            if (!Number.isFinite(spot.charCount) || spot.charCount === 0) {
              spot.charCount = scenarioChars.length;
            }
          }
          if (needsTags && Array.isArray(scenarioDetails?.tags) && scenarioDetails.tags.length > 0) {
            spot.tags = scenarioDetails.tags;
          }
        } catch (error) {
          console.error(`filterSpots: spot ${spotId}`, error);
        }
      }

      const display = this.resolveSpotDisplayFields({
        ...spot,
        chars: scenarioChars,
        tokenName: typeof spot.tokenName === 'string' ? spot.tokenName : (spot.name || ''),
        tokenDescription: descriptionText
      });
      descriptionText = display.description;
      if (hasCharacterPlaceholder(descriptionText)) {
        descriptionText = resolveTextPlaceholders(descriptionText, {
          scenarioChars,
          fallbackCharName: fallbackCharacterName
        });
      }

      toggleHidden(button, !spotMatchesFilters({ ...spot, name: display.name || spot.name, chars: scenarioChars }, {
        searchTerm,
        descriptionText,
        tagIds: this.selectedFilterTagIds,
        charCountFilter: this.charCountFilter
      }, (tags) => tagMappingService.resolveScenarioTagIds(tags)));
    });
  }

  handleCharacterSelected({ selectedCharacterId, character = null }) {
    if (this.pickAdditionalMode) {
      this.addPartyCharacter(selectedCharacterId).catch(console.error);
      return;
    }

    this.state.selectedCharacterId = selectedCharacterId;
    if (character) {
      this.character = character;
    }
    this.show(selectedCharacterId);
  }

  async handleSpotClick(e) {
    const clickedSpotButton = e.currentTarget;
    if (clickedSpotButton?.classList?.contains('is-membership-locked')) {
      const scenarioName = clickedSpotButton.getAttribute('aria-label') || 'This scenario';
      await this.showLockedOutfitMessage(scenarioName, { context: 'multiCharacter' });
      return;
    }

    if (this.lastSpotClick && Date.now() - this.lastSpotClick < 300) {
      return;
    }
    this.lastSpotClick = Date.now();

    const spotButton = e.currentTarget;
    const spotId = spotButton.getAttribute('data-spot-id');
    if (
      spotButton.classList.contains('selected')
      && this.selectedScenarioId != null
      && String(this.selectedScenarioId) === String(spotId)
    ) {
      this.clearScenarioSelection();
      return;
    }
    const spotType = spotButton.getAttribute('data-spot-type');
    const spotName = spotButton.getAttribute('aria-label');
    const conversationData = spotButton.getAttribute('data-conversation-data');

    this.carouselCharacters = [];
    
    try {
      const spot = this.character.publicSpots?.find(s => s.id.toString() === spotId.toString());

      const availableOutfits = spot?.outfits?.length > 0
        ? spot.outfits.map(o => String(o))
        : ['0'];

      let outfitId = '0';
      if (this.state.selectedOutfitId && availableOutfits.includes(String(this.state.selectedOutfitId))) {
        outfitId = String(this.state.selectedOutfitId);
      } else if (availableOutfits.length > 0) {
        outfitId = availableOutfits[0];
      }

      // syncPartyDisplay uses selectedOutfitId for the primary character sprite.
      this.state.selectedOutfitId = outfitId;

      this.selectedScenarioId = spotId;
      const scenarioDetails = await this.getScenarioDetailsCached(spotId);

      const locId = scenarioDetails?.loc_id ?? scenarioDetails?.loc ?? spot?.loc_id ?? 1;
      const time = scenarioDetails?.time || 3;
      const isConversation = conversationData && spotType === 'premium';
      this.isConversationMode = isConversation;

      // Multi-char scenarios fill empty party slots with defaults; single-char keeps current party.
      if (!isConversation && scenarioDetails?.chars?.length > 1) {
        this._fillEmptyPartySlotsFromScenario(scenarioDetails);
      }

      if (isConversation) {
        this.updateCharacterBackground(locId, time);
        await this.loadConversationSprite(conversationData, outfitId, { withBackdrop: true });
        await this.renderPartySlots();
      } else if (this.partyCharacterIds.length > 1 || scenarioDetails?.chars?.length > 1) {
        this.updateCharacterBackground(locId, time);
        await this.syncPartyDisplay();
      } else {
          const spriteUrl = await this.service.getScenarioSprite(this.character.id, spotId, DEFAULT_CHARACTER_PLACEHOLDER, outfitId);
          this.character.modalImage = spriteUrl;
          const bgElement = this.getSharedBackgroundElement();
          const imgElement = /** @type {HTMLImageElement | null} */ (
            this.characterContent.querySelector('.character-primary-column .character-image')
            || this.characterContent.querySelector('.character-image')
          );
          const backgroundUrl = getBackgroundService().constructBackgroundUrl(locId, time);

          if (!imgElement) {
            this.updateCharacterBackground(locId, time);
            this.updateCharacterImage();
          } else {
            const requestKey = `${spotId}-${outfitId}`;
            this._lastSpotImageRequestKey = requestKey;
            const hasBridge = imgElement.src && imgElement.src.length > 0;
            if (!hasBridge) imgElement.style.opacity = '0';

            const bgReady = backgroundUrl
              ? whenImageReady(backgroundUrl, bgElement)
              : Promise.resolve();
            const imgReady = whenImageReady(spriteUrl, imgElement);

            Promise.allSettled([bgReady, imgReady])
              .then(([bgResult, imgResult]) => {
                if (this._lastSpotImageRequestKey !== requestKey) return;

                if (bgResult.status === 'fulfilled' && backgroundUrl && bgElement) {
                  bgElement.src = backgroundUrl;
                  toggleHidden(bgElement, false);
                } else if (bgResult.status === 'rejected') {
                  toggleHidden(bgElement, true);
                  console.error('Failed to load spot background:', bgResult.reason);
                } else if (bgElement) {
                  toggleHidden(bgElement, true);
                }

                if (imgResult.status === 'fulfilled') {
                  imgElement.src = spriteUrl;
                  imgElement.style.opacity = '1';
                } else if (imgResult.status === 'rejected') {
                  console.error('Failed to load spot sprite:', imgResult.reason);
                  const fallbackSrc = this.character.image !== spriteUrl
                    ? this.character.image
                    : DEFAULT_CHARACTER_PLACEHOLDER;
                  loadFallbackImage(imgElement, fallbackSrc);
                }
              });
          }

          /** @type {HTMLElement} */
          const header = /** @type {HTMLElement} */ (this.characterContent.querySelector('.character-header h2'));
          if (header) {
            header.innerHTML = this.character.name;
            header.style.color = '';
          }
          await this.renderPartySlots();
      }

      this.characterContent.querySelectorAll('.spot-button').forEach(btn => {
        btn.classList.remove('selected');
      });
      spotButton.classList.add('selected');

      this.selectedScenarioId = spotId;
      this.selectedConversationData = conversationData;

      await this.renderTraits();
      this.renderGoButton();
    } catch (error) {
      console.error('handleSpotClick:', error);
    }
  }

  async updateCharacterImage() {
    /** @type {HTMLImageElement} */
    const imgElement = /** @type {HTMLImageElement} */ (
      this.characterContent.querySelector('.character-primary-column .character-image')
      || this.characterContent.querySelector('.character-image')
    );
    if (!imgElement || !this.character.modalImage) return;

    const hasBridge = imgElement.src && imgElement.src.length > 0;
    if (!hasBridge) imgElement.style.opacity = '0';

    try {
      await whenImageReady(this.character.modalImage, imgElement);
      imgElement.src = this.character.modalImage;
      imgElement.style.opacity = '1';
    } catch (error) {
      console.error('Failed to load sprite:', this.character.modalImage, error);
      const fallbackSrc = this.character.image !== this.character.modalImage
        ? this.character.image
        : DEFAULT_CHARACTER_PLACEHOLDER;
      loadFallbackImage(imgElement, fallbackSrc);
    }
  }

  updateCharacterBackground(locId, time = 3) {
    if (!locId) return;

    const bgElement = this.getSharedBackgroundElement();
    if (!bgElement) return;

    const backgroundUrl = getBackgroundService().constructBackgroundUrl(locId, time);

    if (backgroundUrl) {
      bgElement.src = backgroundUrl;
      toggleHidden(bgElement, false);
    } else {
      toggleHidden(bgElement, true);
    }
  }

  async handleConfirmStart() {
    if (!this.selectedScenarioId) return;

    if (this.state.selectedOutfitId) {
      const traitsList = this.characterContent.querySelector('#outfit-list');
      const selectedElement = traitsList?.querySelector(`[data-outfit-id="${this.state.selectedOutfitId}"]`);
      if (selectedElement && selectedElement.classList.contains('locked-outfit')) {
        const outfitName = await this.service.getOutfitName(this.state.selectedOutfitId);
        const lockReason = selectedElement.dataset.lockReason;
        if (lockReason === 'character') {
          await this.showCharacterMissingOutfitMessage(outfitName);
        } else {
          await this.showLockedOutfitMessage(outfitName, { context: 'outfit' });
        }
        return;
      }
    }

    const conversationData = this.selectedConversationData;

    let scenarioTime = null;
    let scenarioLocId = null;
    let selectedScenario = null;
    if (this.selectedScenarioId) {
      try {
        selectedScenario = await this.getScenarioDetailsCached(this.selectedScenarioId);
        scenarioTime = selectedScenario?.time;
        const rawLoc = selectedScenario?.loc_id ?? selectedScenario?.loc;
        if (rawLoc && Number(rawLoc) !== 0) scenarioLocId = rawLoc;
      } catch (error) {
        console.warn('handleConfirmStart: scenario details', error);
      }
    }

    if (conversationData) {
      try {
        const conversation = parseConversationData(conversationData);
        if (!conversation) throw new Error('INVALID_CONVERSATION_DATA');

        if (!scenarioTime && conversation.scenarioId) {
          try {
            if (!selectedScenario) {
              selectedScenario = await this.getScenarioDetailsCached(conversation.scenarioId);
            }
            scenarioTime = selectedScenario?.time;
          } catch (error) {
            console.warn('handleConfirmStart: scenario time', error);
          }
        }

        // Hangout hydrates the full transcript via GET /conversation.
        const hangoutConfig = buildResumeConfig({
          conversation,
          character: this.character,
          carouselCharacters: this.carouselCharacters,
          selectedOutfitId: this.state.selectedOutfitId,
          normalizedMessages: [],
          scenarioTime,
          selectedScenario
        });
        await storageManager.setItem('currentHangoutConfig', hangoutConfig);
        window.location.href = HANGOUT_URL;

      } catch (error) {
        console.error('handleConfirmStart: resume', error);
        window.location.href = HANGOUT_URL;
      }
    } else {
      if (!getNsfwEnabled() && this.selectedScenarioId) {
        const blockedScenario = selectedScenario
          || this.character?.publicSpots?.find((spot) => String(spot.id) === String(this.selectedScenarioId))
          || null;
        if (scenarioIsNsfw(blockedScenario)) {
          showToast(getBlockedNsfwScenarioMessage(blockedScenario), 'exclamation-triangle');
          return;
        }
      }

      if (this.partyCharacterIds.length > 1 && this.carouselCharacters.length < 2 && this.selectedScenarioId) {
        await this.syncPartyDisplay();
      }

      const hangoutConfig = buildNewConversationConfig({
        scenarioId: this.selectedScenarioId,
        character: this.character,
        carouselCharacters: this.carouselCharacters,
        selectedOutfitId: this.state.selectedOutfitId,
        isPrivateScenario: this.getActiveSpotsView() === 'private',
        scenarioTime,
        loc_id: scenarioLocId,
        selectedScenario
      });
      await storageManager.setItem('currentHangoutConfig', hangoutConfig);
      window.location.href = HANGOUT_URL;
    }
  }


  destroy() {
    if (this.element) this.element.removeEventListener('click', this.handleOutsideClick);
    if (this.retryButton) this.retryButton.removeEventListener('click', this.handleRetry);
    this.characterContent?.removeEventListener('scroll', this.handleCharacterContentScroll);
    document.removeEventListener('keydown', this.handleEscapeKey);

    document.removeEventListener('languageChanged', this.handleLanguageChange);
    document.removeEventListener('mappingsUpdated', this.handleMappingsUpdated);

    eventBus.off(EVENTS.RANKINGS.CHARACTER_SELECTED, this.handleCharacterSelectedBound);
    this.unsubscribeBlockedContent?.();
    this.unsubscribeBlockedContent = null;
    closeContentModerationMenu();
    document.querySelector('.description-tooltip')?.remove();

    super.destroy();
  }

  toggleSearchBar() {
    const { panelId } = getSpotsViewDom(this.getActiveSpotsView());
    /** @type {HTMLElement} */
    const filtersPanel = /** @type {HTMLElement} */ (this.characterContent.querySelector(`#${panelId}`)
      || this.characterContent.querySelector('.spot-filters-panel'));

    if (!filtersPanel) return;

    const willOpen = !filtersPanel.classList.contains('active');
    filtersPanel.classList.toggle('active', willOpen);
    this.spotFiltersPanelOpen = willOpen;

    if (willOpen) {
      this._syncCharCountFromParty();
      this._refreshSpotFiltersPanel(filtersPanel);
      this.applyActiveScenarioFilters();
      const searchBar = /** @type {HTMLInputElement} */ (filtersPanel.querySelector('.spot-search-bar'));
      searchBar?.focus();
    }
  }

  async syncPartyDisplay() {
    if (!this.selectedScenarioId) {
      await this.renderPartySlots();
      return;
    }

    const scenarioDetails = await this.getScenarioDetailsCached(this.selectedScenarioId);

    this._normalizePartyIds();
    if (!this.partyCharacterIds.length && this.character?.id) {
      this.partyCharacterIds = [this.character.id];
    }

    if (this.partyCharacterIds.length < 1) {
      this.carouselCharacters = [];
      await this.renderPartySlots();
      return;
    }

    this.carouselCharacters = await buildCarouselCharacters({
      primaryCharacter: this.character,
      scenarioDetails: scenarioDetails || {},
      service: this.service,
      rankingsState: this.rankingsState,
      selectedOutfitId: this.state.selectedOutfitId,
      scenarioId: this.selectedScenarioId,
      partyCharacterIds: this.partyCharacterIds
    });

    const time = scenarioDetails?.time || 3;
    this.updateCharacterBackground(scenarioDetails?.loc_id ?? scenarioDetails?.loc, time);

    const primary = this.carouselCharacters.find(
      c => String(c?.id) === String(this.characterId || this.character?.id)
    ) || this.carouselCharacters[0];

    if (primary) this._applyPrimaryCarouselCharacter(primary);

    await this.renderPartySlots();
    await this.renderTraits();
  }

  _applyPrimaryCarouselCharacter(primary) {
    if (!primary) return;
    if (primary.id != null) {
      this.characterId = primary.id;
      this.state.selectedCharacterId = primary.id;
      if (this.character) this.character.id = primary.id;
    }
    if (primary.name && this.character) {
      this.character.name = primary.name;
    }
    this.character.modalImage = primary.modalImage;
    if (primary.outfitId != null) {
      this.state.selectedOutfitId = String(primary.outfitId);
    }
    this.updateCharacterImage();

    const header = /** @type {HTMLElement | null} */ (
      this.characterContent.querySelector('.character-primary-column .character-name-header')
      || this.characterContent.querySelector('.character-primary-column .character-header h2')
    );
    if (header && primary.name) {
      header.textContent = primary.name;
    }
  }

  async initializeConversationCarousel(conversation) {
    try {
      this.isConversationMode = true;
      this.carouselCharacters = [];
      
      if (!conversation.allCharacters || conversation.allCharacters.length < 2) {
        await this.renderPartySlots();
        return;
      }

      this.carouselCharacters = await buildConversationCarouselCharacters({
        conversation,
        rankingsState: this.rankingsState,
        service: this.service
      });

      // Move opened character to slot 0 (reorder, do not duplicate).
      const openedId = this.characterId || conversation.characterId;
      if (openedId != null && this.carouselCharacters.length > 1) {
        const openedIndex = this.carouselCharacters.findIndex(
          c => String(c.id) === String(openedId)
        );
        if (openedIndex > 0) {
          const selected = this.carouselCharacters[openedIndex];
          const others = this.carouselCharacters.filter((_, i) => i !== openedIndex);
          this.carouselCharacters = [selected, ...others];
        }
      }
      this.carouselCharacters = this.carouselCharacters.slice(0, MAX_PARTY_SIZE);

      this.partyCharacterIds = this.carouselCharacters
        .map(c => c.id)
        .filter(id => id != null)
        .slice(0, MAX_PARTY_SIZE);

      const finalState = reconstructFinalStateFromConversation(conversation);
      let scenarioDetails = null;
      try {
        if (conversation.scenarioId && conversation.scenarioId !== 'unknown') {
          scenarioDetails = await this.getScenarioDetailsCached(conversation.scenarioId);
        }
      } catch {
        scenarioDetails = null;
      }
      const { locId, timeSlot } = resolveConversationBackdrop(scenarioDetails, finalState);
      this.updateCharacterBackground(locId, timeSlot);

      this._applyPrimaryCarouselCharacter(this.carouselCharacters[0]);

      await this.renderPartySlots();
      await this.renderTraits();
    } catch (error) {
      console.error('initializeConversationCarousel:', error);
      await this.renderPartySlots();
    }
  }

  filterConversations() {
    /** @type {HTMLInputElement} */
    const searchBar = /** @type {HTMLInputElement} */ (this.characterContent.querySelector('#premium-spot-search'));
    const searchTerm = searchBar.value.toLowerCase().trim();
    const spotRow = this.characterContent.querySelector('#premium-spot-row');

    if (!spotRow) return;

    /** @type {NodeListOf<HTMLElement>} */
    const conversationButtons = /** @type {NodeListOf<HTMLElement>} */ (spotRow.querySelectorAll('.conversation-button'));
    conversationButtons.forEach(button => {
      const lastMessage = button.querySelector('.conversation-last-message')?.textContent || '';

      const messageMatch = lastMessage.toLowerCase().includes(searchTerm);

      toggleHidden(button, !(searchTerm === '' || messageMatch));
    });
  }

  filterPrivateScenarios() {
    /** @type {HTMLInputElement} */
    const searchBar = /** @type {HTMLInputElement} */ (this.characterContent.querySelector('#private-spot-search'));
    const searchTerm = (searchBar?.value || '').toLowerCase().trim();
    const spotRow = this.characterContent.querySelector('#private-scenarios-row');

    if (!spotRow) return;

    const cachedPrivate = (this.service?.rankingsState?.scenarios || []).filter((s) => s.is_private);

    /** @type {NodeListOf<HTMLElement>} */
    const scenarioButtons = /** @type {NodeListOf<HTMLElement>} */ (spotRow.querySelectorAll('.spot-button'));
    scenarioButtons.forEach(button => {
      const spotId = button.getAttribute('data-spot-id');
      const scenario = cachedPrivate.find((s) => String(s.id) === String(spotId));
      const resolvedPrivate = scenario
        ? resolveScenarioRecordDisplay(
          scenario,
          [this.rankingsState?.characters, this.character],
          this.getSelectedPartyDisplayChars()
        )
        : null;
      const scenarioName = button.getAttribute('data-full-name')
        || button.querySelector('.spot-title')?.getAttribute('data-full-name')
        || button.getAttribute('aria-label')
        || button.querySelector('.spot-title')?.textContent?.trim()
        || resolvedPrivate?.name
        || '';
      const scenarioDescription = button.querySelector('.spot-description')?.textContent
        || resolvedPrivate?.description
        || '';

      const spot = {
        name: resolvedPrivate?.name || scenarioName,
        description: scenarioDescription,
        tags: scenario?.tags || [],
        chars: scenario?.chars,
        charCount: Array.isArray(scenario?.chars) ? scenario.chars.length : (scenario?.charCount || 0)
      };

      toggleHidden(button, !spotMatchesFilters(spot, {
        searchTerm,
        descriptionText: scenarioDescription,
        tagIds: this.selectedFilterTagIds,
        charCountFilter: this.charCountFilter
      }, (tags) => tagMappingService.resolveScenarioTagIds(tags)));
    });
  }

  async handleShuffleScenarios() {
    if (!this.character?.publicSpots?.length) return;
    this.character.publicSpots = shuffleArray(this.character.publicSpots);
    await this.renderPublicSpots();
  }

  async resolveScenarioTooltipCharacterName(char) {
    const charId = char?.id ?? char?.character_id;
    const fromJson = pickScenarioCharJsonName(char);
    if (fromJson && !isPlaceholderCharacterName(fromJson, charId)) return fromJson;

    const fromState = this.rankingsState?.characters?.find((c) => String(c.id) === String(charId));
    const stateName = typeof fromState?.name === 'string' ? fromState.name.trim() : '';
    if (stateName && !isPlaceholderCharacterName(stateName, charId)) return stateName;

    if (
      charId != null
      && String(charId) === String(this.character?.id)
      && this.character?.name
      && !isPlaceholderCharacterName(this.character.name, charId)
    ) {
      return this.character.name;
    }

    const fromCatalog = await this.service.getPublicCharacterNameById(charId);
    if (fromCatalog) return fromCatalog;

    return charId != null && charId !== '' ? `Character ${charId}` : (this.character?.name || 'Character');
  }

  async appendDescriptionTooltipCast(tooltip, scenarioChars) {
    if (!tooltip || !Array.isArray(scenarioChars) || scenarioChars.length === 0) return;

    const cast = document.createElement('div');
    cast.className = 'description-tooltip-cast';

    for (const char of scenarioChars) {
      const name = await this.resolveScenarioTooltipCharacterName(char);
      const outfitIds = listedOutfitIdsForScenarioChar(char);
      const outfitNames = await Promise.all(
        outfitIds.map((outfitId) => this.service.getOutfitName(outfitId))
      );
      const line = document.createElement('p');
      line.className = 'description-tooltip-cast-line';
      line.textContent = outfitNames.length ? `${name}: ${outfitNames.join(', ')}` : name;
      cast.appendChild(line);
    }

    tooltip.appendChild(cast);
  }

  async appendDescriptionTooltipLocation(tooltip, locId) {
    if (!tooltip || locId == null || locId === '' || Number(locId) === 0) return;

    const locationName = await getLocationNameById(locId, '');
    if (!locationName) return;

    const t = this._rankingsCopy();
    const locationLine = document.createElement('p');
    locationLine.className = 'description-tooltip-location';
    locationLine.textContent = interpolate(
      t['rankings.modal.startLocation'] || 'Start location: {name}',
      { name: locationName }
    );
    tooltip.appendChild(locationLine);
  }

  handleBlockedContentChanged(detail) {
    if (!detail || detail.action === 'load') return;
    if (detail.kind === 'character' && isCharacterBlocked(this.characterId) && this.isVisible()) {
      this.hide({ clearParty: true });
      this.removeDescriptionTooltip();
      return;
    }
    if (this.character?.publicSpots) {
      this.character.publicSpots = filterBlockedScenarios(this.character.publicSpots);
    }
    if (this.isVisible() && this.getActiveSpotsView() === 'scenarios') {
      const contentContainer = this.characterContent?.querySelector('.spots-content-container');
      if (contentContainer) void this.renderScenariosView(contentContainer, this.spotsRenderGeneration);
    }
  }

  appendCharacterInfoExpandIcon(imageWrap, characterId, characterName) {
    if (!imageWrap || characterId == null) return;
    imageWrap.querySelector('.description-expand-icon')?.remove();
    const t = this._rankingsCopy();
    const expandIcon = document.createElement('button');
    expandIcon.type = 'button';
    expandIcon.className = 'description-expand-icon';
    expandIcon.innerHTML = '&#8505;';
    expandIcon.tabIndex = 0;
    expandIcon.setAttribute(
      'aria-label',
      t['rankings.moderation.characterInfo'] || `About ${characterName || 'character'}`
    );
    expandIcon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      void this.showCharacterInfoModal(characterId, characterName, expandIcon);
    });
    expandIcon.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      void this.showCharacterInfoModal(characterId, characterName, expandIcon);
    });
    imageWrap.appendChild(expandIcon);
  }

  removeDescriptionTooltip() {
    document.querySelector('.description-tooltip')?.remove();
    closeContentModerationMenu();
  }

  createDescriptionTooltipShell(title, moderation = null) {
    this.removeDescriptionTooltip();
    const tooltip = document.createElement('div');
    tooltip.className = 'description-tooltip';
    const header = document.createElement('div');
    header.className = 'description-tooltip-header';
    const titleElement = document.createElement('h4');
    titleElement.className = 'description-tooltip-title';
    titleElement.textContent = title;
    header.appendChild(titleElement);
    if (moderation?.contentId != null && String(moderation.contentId) !== '') {
      appendModerationKebab(header, moderation);
    }
    tooltip.appendChild(header);
    tooltip.addEventListener('click', (event) => event.stopPropagation());
    return tooltip;
  }

  positionDescriptionTooltip(tooltip, iconElement) {
    if (!iconElement) return;
    const iconRect = iconElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width;
    const spaceOnLeft = iconRect.left;
    const spaceOnRight = window.innerWidth - iconRect.right;

    if (spaceOnLeft >= tooltipWidth + 10) {
      tooltip.style.right = `${window.innerWidth - iconRect.left + 10}px`;
      tooltip.style.top = `${iconRect.top}px`;
    } else if (spaceOnRight >= tooltipWidth + 10) {
      tooltip.style.left = `${iconRect.right + 10}px`;
      tooltip.style.top = `${iconRect.top}px`;
    } else {
      tooltip.style.left = '50%';
      tooltip.style.top = `${iconRect.bottom + 10}px`;
      tooltip.style.transform = 'translateX(-50%)';
    }
  }

  attachDescriptionTooltipDismiss(tooltip) {
    const closeTooltip = (event) => {
      if (event?.type === 'click' && event.target instanceof Element) {
        if (
          tooltip.contains(event.target)
          || event.target.closest('.content-moderation-menu')
          || event.target.closest('.description-tooltip-menu-btn')
          || event.target.closest('.description-expand-icon')
        ) {
          return;
        }
      }
      tooltip.remove();
      closeContentModerationMenu();
      document.removeEventListener('click', closeTooltip);
      document.removeEventListener('keydown', handleEscape);
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') closeTooltip(e);
    };

    setTimeout(() => {
      document.addEventListener('click', closeTooltip);
      document.addEventListener('keydown', handleEscape);
    }, 100);
  }

  async showCharacterInfoModal(characterId, characterName, iconElement) {
    const t = this._rankingsCopy();
    const tooltip = this.createDescriptionTooltipShell(characterName || t['rankings.moderation.characterInfo'] || 'Character', {
      contentKind: 'character',
      contentId: characterId
    });

    try {
      const details = await fetchCache.fetchCharacterDetails(characterId, this.isPrivateCharacter, getCurrentLanguage() || 'en');
      const resolvedName = pickResolvedCharacterName(details, characterId)
        || (!isPlaceholderCharacterName(characterName, characterId) ? String(characterName || '').trim() : '');
      if (resolvedName) {
        const titleEl = tooltip.querySelector('.description-tooltip-title');
        if (titleEl) titleEl.textContent = resolvedName;
        if (this.character && String(this.character.id) === String(characterId)) {
          this.character.name = resolvedName;
        }
        const header = this.characterContent?.querySelector('.character-primary-column .character-name-header');
        if (header && String(this.characterId) === String(characterId)) {
          header.textContent = resolvedName;
        }
      }
      const info = details?.info && typeof details.info === 'object' && !Array.isArray(details.info)
        ? details.info
        : {};
      const entries = collectInfoEntries(info);
      if (entries.length) {
        const list = document.createElement('dl');
        list.className = 'description-tooltip-info';
        entries.forEach((entry) => {
          const dt = document.createElement('dt');
          dt.textContent = entry.label;
          const dd = document.createElement('dd');
          dd.textContent = entry.text;
          list.appendChild(dt);
          list.appendChild(dd);
        });
        tooltip.appendChild(list);
      }

      let outfitIds = this.characterOutfitsCache.get(characterId);
      if (!outfitIds) {
        outfitIds = await this.service.getCharacterOutfits(characterId);
        if (outfitIds) this.characterOutfitsCache.set(characterId, outfitIds);
      }
      const ids = outfitIds instanceof Set ? [...outfitIds] : Array.isArray(outfitIds) ? outfitIds : [];
      if (ids.length) {
        const names = (await Promise.all(ids.map((id) => this.service.getOutfitName(id))))
          .filter(Boolean);
        if (names.length) {
          const outfitsLine = document.createElement('p');
          outfitsLine.className = 'description-tooltip-outfits';
          outfitsLine.textContent = interpolate(
            t['rankings.moderation.ownedOutfits'] || 'Outfits: {names}',
            { names: names.join(', ') }
          );
          tooltip.appendChild(outfitsLine);
        }
      }
    } catch (error) {
      console.error('showCharacterInfoModal', error);
      const fallback = document.createElement('p');
      fallback.className = 'description-tooltip-text';
      fallback.textContent = t['rankings.moderation.characterInfoUnavailable'] || 'Character details are unavailable.';
      tooltip.appendChild(fallback);
    }

    document.body.appendChild(tooltip);
    this.positionDescriptionTooltip(tooltip, iconElement);
    requestAnimationFrame(() => tooltip.classList.add('is-visible'));
    this.attachDescriptionTooltipDismiss(tooltip);
  }

  async showDescriptionModal(title, description, iconElement) {
    const spotButton = iconElement?.closest?.('.spot-button');
    const spotId = spotButton?.getAttribute('data-spot-id') || this.selectedScenarioId;
    const skipScenarioTags = isConversationLastMessageTooltip(spotButton, title);
    const tooltip = this.createDescriptionTooltipShell(title, skipScenarioTags ? null : {
      contentKind: 'scenario',
      contentId: spotId
    });

    const scenarioRecord = this._findScenarioRecordOnPage(spotId);
    const fallbackCharacterName = this.character?.name || 'Character';
    let scenarioChars = Array.isArray(scenarioRecord?.chars) ? scenarioRecord.chars : [];
    let rawTags = Array.isArray(scenarioRecord?.tags) ? scenarioRecord.tags : [];
    let locId = scenarioRecord?.loc_id ?? scenarioRecord?.loc ?? null;

    if (spotId) {
      try {
        const scenarioDetails = await this.getScenarioDetailsCached(spotId);
        if (Array.isArray(scenarioDetails?.chars) && scenarioDetails.chars.length > 0) {
          scenarioChars = scenarioDetails.chars;
        }
        if (rawTags.length === 0 && Array.isArray(scenarioDetails?.tags)) {
          rawTags = scenarioDetails.tags;
        }
        const detailsLoc = scenarioDetails?.loc_id ?? scenarioDetails?.loc;
        if (detailsLoc != null && detailsLoc !== '') locId = detailsLoc;
      } catch (error) {
        console.error(`showDescriptionModal: spot ${spotId}`, error);
      }
    }

    if (!skipScenarioTags) {
      await this.appendDescriptionTooltipCast(tooltip, scenarioChars);
    }

    const displayChars = overlaySelectedCharacterSlots(
      scenarioChars,
      skipScenarioTags ? [] : this.getSelectedPartyDisplayChars()
    );
    const resolvedTitle = resolveTextPlaceholders(title || '', {
      scenarioChars: displayChars,
      fallbackCharName: fallbackCharacterName
    });
    const titleEl = tooltip.querySelector('.description-tooltip-title');
    if (titleEl && resolvedTitle) titleEl.textContent = resolvedTitle;

    const processedDescription = resolveTextPlaceholders(description || '', {
      scenarioChars: displayChars,
      fallbackCharName: fallbackCharacterName
    });

    const descriptionElement = document.createElement('p');
    descriptionElement.className = 'description-tooltip-text';
    descriptionElement.textContent = processedDescription;
    tooltip.appendChild(descriptionElement);

    if (!skipScenarioTags) {
      await this.appendDescriptionTooltipLocation(tooltip, locId);
    }

    if (!skipScenarioTags && rawTags.length > 0) {
      const options = await this._ensureScenarioFilterTagOptions();
      const tagIds = tagMappingService.resolveScenarioTagIds(rawTags);
      const labelById = new Map(options.map((opt) => [Number(opt.id), opt.label]));
      const labels = tagIds.map((id) => labelById.get(Number(id))).filter(Boolean);
      appendDescriptionTooltipTags(tooltip, labels);
    }

    document.body.appendChild(tooltip);
    this.positionDescriptionTooltip(tooltip, iconElement);
    requestAnimationFrame(() => tooltip.classList.add('is-visible'));
    this.attachDescriptionTooltipDismiss(tooltip);
  }

} 
